/**
 * DialerAdminPanels.jsx — Prompt-2 redesigned admin views for the Sales Dialer.
 *
 * Exports:
 *   AdminCampaignsPanel — clickable rows with in-place 2-column detail panel
 *   AdminAgentsPanel    — status tiles + Actions column (Reassign) + detail side-panel + Invite
 *   AdminAnalyticsPanel — date/campaign/agent filters + per-campaign perf table + charts
 *   AssignAgentsStep    — NEW 4th step for the New-Campaign modal
 *   ReviewLaunchStep    — rewritten 5th step summary card
 *   scriptCardEnhancements → renderScriptPerfRow / renderScriptSectionChips
 *
 * Demo mode was removed Feb 2026. All panels hit the real /api/dialer/*
 * endpoints; no demo fixtures or no-op toasts remain.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronDown, ChevronRight, Play, Pause, Pencil, FileText, Users,
  BarChart3, Archive, Filter, TrendingUp, Target, Zap, UserPlus, X, Calendar,
  RotateCcw, Check,
} from "lucide-react";
import RetargetCampaignModal from "@/components/RetargetCampaignModal";
import { CampaignSearchFilter } from "@/components/CampaignSearchFilter";
import CardTip from "@/components/CardTip";

const API_BASE = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
const STATUS_STYLES = {
  active:  { border: "#1D9E75", bg: "#DAFBE8", color: "#1D9E75" },
  paused:  { border: "#BA7517", bg: "#FFF3CD", color: "#856404" },
  draft:   { border: "#888",    bg: "#f5f5f5", color: "#888" },
  archived:{ border: "#A32D2D", bg: "#FCEBEB", color: "#A32D2D" },
};

// Iter 178.6 (Danny Feb 2026) — product taxonomy lives in
// `lib/productTaxonomy.js` so the admin Campaigns tab, the agent
// CampaignGroupedQueue, and the campaign create/edit form all read
// from the same source of truth. See that file for the taxonomy +
// alias matching + canonical-write logic.
import {
  PRODUCT_CATEGORIES,
  PRODUCT_OTHER,
  categorizeCampaign,
  categoryCodesForCampaign,
  getCategory,
  canonicalAliasForCode,
  normalizeProductCode,
} from "@/lib/productTaxonomy";

// ═════════════ CAMPAIGNS ═════════════
export function AdminCampaignsPanel({
  campaigns, scripts, agents, token,
  onRefreshCampaigns, onOpenEdit, onNavigateToLeads, onSwitchToAnalytics,
}) {
  const [expandedId, setExpandedId] = useState(() => {
    try { return sessionStorage.getItem("dialer_admin_expanded_campaign") || null; } catch (_) { return null; }
  });
  const [retargetCampaign, setRetargetCampaign] = useState(null);
  // Iter 175.6 (Danny Tier 2 #7) — status-bucket tab state. Persisted
  // in sessionStorage so the admin's last view sticks across renders.
  const [campaignsTab, _setCampaignsTab] = useState(() => {
    try { return sessionStorage.getItem("dialer_admin_campaigns_tab") || "active"; }
    catch (_) { return "active"; /* sessionStorage may be unavailable */ }
  });
  const setCampaignsTab = (t) => {
    _setCampaignsTab(t);
    try { sessionStorage.setItem("dialer_admin_campaigns_tab", t); } catch (_) { /* noop */ }
  };
  // Iter 175.7 (Lisa feature request `ffa434e1`) — secondary filters
  // for the admin campaign list. Not persisted (transient per session).
  const [campaignsCreatorFilter, setCampaignsCreatorFilter] = useState("all");
  const [campaignsProductFilter, setCampaignsProductFilter] = useState("all");
  // Iter 176 Phase 4 (Danny, scoring_and_dialer_updates.md §4) —
  // Per-rep filter for campaign stats. `repFilter` is a Set of agent
  // ids; empty Set = "All reps" (default). Multi-select via shift+click
  // (handled in the rep-picker chip). `perRepStats` is the lazy-loaded
  // {campaign_id: {agent_id: {contacted, connected, meetings}}} map
  // fetched from /api/dialer/campaigns/per-rep-stats.
  const [repFilter, setRepFilter] = useState(() => new Set());
  const [perRepStats, setPerRepStats] = useState({});
  const [perRepReps, setPerRepReps] = useState([]);
  const [repFilterOpen, setRepFilterOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_BASE}/dialer/campaigns/per-rep-stats`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        setPerRepStats(data?.stats || {});
        setPerRepReps(data?.active_reps || []);
      } catch (_e) {
        // Non-fatal — rep filter just won't appear. Don't toast.
      }
    })();
    return () => { cancelled = true; };
  }, [token]);
  // Iter 142 — View-leads lightbox (in-place instead of navigating away)
  const [leadsLightbox, setLeadsLightbox] = useState(null); // { campaign, loading, items, total, error }
  // Iter 158 (Josh) — Agents roster lightbox (in-place; replaces the
  // mislabeled "Start all agents" button which used to silently flip the
  // campaign status. Now this opens a panel showing assigned agents +
  // their live status, plus a primary "Activate campaign" CTA.)
  const [agentsLightbox, setAgentsLightbox] = useState(null); // { campaign, agents }
  // Iter 158 (Josh) — audience snapshot in-flight indicator per campaign
  const [snapshotting, setSnapshotting] = useState({}); // { [campaign_id]: true }
  const toggle = (cid) => {
    const next = expandedId === cid ? null : cid;
    setExpandedId(next);
    try { next ? sessionStorage.setItem("dialer_admin_expanded_campaign", next) : sessionStorage.removeItem("dialer_admin_expanded_campaign"); } catch (_) {}
  };

  // Iter 178.6 (Danny Feb 2026) — collapsed-state per product group.
  // Persist in sessionStorage so admins keep their preferred view
  // (e.g., if they always work CCM, they can collapse the others).
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const raw = sessionStorage.getItem("dialer_admin_collapsed_groups");
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  const toggleGroup = (code) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      try { sessionStorage.setItem("dialer_admin_collapsed_groups", JSON.stringify(Array.from(next))); } catch { /* sessionStorage may be unavailable */ }
      return next;
    });
  };

  // Iter 178.8 (Josh Feb 2026) — inline product-tag editor on each
  // admin campaign row. `tagMenuFor` holds the campaign id whose
  // "add tag" picker is currently open (null = closed). We persist
  // tags to `filters.product_interest` using the canonical alias
  // for each code (lowercase, taxonomy-defined) so the round-trip
  // is stable across reload.
  const [tagMenuFor, setTagMenuFor] = useState(null);

  const toggleProductTag = async (campaign, code) => {
    const alias = canonicalAliasForCode(code);
    if (!alias) return;
    const currentRaw = Array.isArray(campaign?.filters?.product_interest)
      ? campaign.filters.product_interest
      : [];
    // Canonical comparison: an existing tag matches if it normalizes
    // to the same code we're toggling. Lets us drop legacy variants
    // (e.g. "ccm/apcm") when the user removes APCM cleanly.
    const filtered = currentRaw.filter((raw) => {
      const c = normalizeProductCode(raw);
      return c !== code;
    });
    const wasOn = filtered.length !== currentRaw.length;
    const nextRaw = wasOn ? filtered : [...currentRaw, alias];
    const nextFilters = { ...(campaign.filters || {}), product_interest: nextRaw };
    try {
      await patchCampaign(
        campaign.id,
        { filters: nextFilters },
        wasOn ? `Removed ${code}` : `Tagged ${code}`,
      );
    } catch (e) {
      toast.error(`Couldn't update tags: ${e?.message || e}`);
    }
  };

  const patchCampaign = async (cid, body, successMsg) => {
    try {
      await axios.patch(`${API_BASE}/dialer/campaigns/${cid}`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(successMsg);
      onRefreshCampaigns?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  // Iter 142 — open the in-place "View leads" lightbox
  // Iter 223 (Aug 2026) — raise the lead fetch cap from 500 → 10 000.
  // Most campaigns are configured for well under 10 k leads, so bumping
  // the cap prevents misleading truncated counts (e.g. a 900-lead
  // audience showing only 500) without exposing an unbounded scan.
  const openLeadsLightbox = async (c) => {
    setLeadsLightbox({ campaign: c, loading: true, items: [], total: 0 });
    try {
      const { data } = await axios.get(`${API_BASE}/dialer/campaigns/${c.id}/leads`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 10000 },
      });
      setLeadsLightbox({ campaign: c, loading: false, items: data.items || [], total: data.total || 0, source: data.source });
    } catch (err) {
      setLeadsLightbox({ campaign: c, loading: false, items: [], total: 0, error: err?.response?.data?.detail || "Couldn't load leads" });
    }
  };

  // Iter 158 (Josh) — open the in-place Agents roster for a campaign.
  // Looks up the campaign's `agent_ids` against the global `agents` list
  // already loaded by the parent SalesDialer page (so it's instant; no
  // extra API call). Replaces the old "Start all agents" button which
  // silently set status=active without giving the admin any visibility.
  const openAgentsLightbox = (c) => {
    const roster = (c.agent_ids || []).map(id => agents.find(a => a.id === id)).filter(Boolean);
    setAgentsLightbox({ campaign: c, agents: roster });
  };

  // Iter 158 (Josh) — Re-link audience: snapshots lead_ids onto the
  // campaign from its current filter shape. Required for legacy
  // campaigns created before snapshot-at-create was wired (Feb 2026).
  const snapshotAudience = async (c) => {
    if (snapshotting[c.id]) return;
    setSnapshotting(s => ({ ...s, [c.id]: true }));
    try {
      const { data } = await axios.post(
        `${API_BASE}/dialer/campaigns/${c.id}/snapshot-audience`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Audience linked: ${data.lead_count} leads`);
      onRefreshCampaigns?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Snapshot failed";
      if (/No filters set/i.test(msg)) {
        toast.error("This campaign has no filters set. Open Edit → Audience to define a target audience first.");
      } else {
        toast.error(msg);
      }
    } finally {
      setSnapshotting(s => ({ ...s, [c.id]: false }));
    }
  };

  // Iter 165 (Danny report fix) — explicit "Start campaign" action.
  //
  // Bug: clicking "Resume campaign" on a freshly-created draft set
  // status=active but the campaign never showed up under the assigned
  // agents' queue because the lead queue items were never generated.
  // `CampaignGroupedQueue` filters campaigns to only those that appear
  // in the rep's `workQueue`, so a campaign with 0 queue items is
  // invisible to its assigned agents.
  //
  // Fix:
  //   1. PATCH status → active.
  //   2. POST /dialer/campaigns/{id}/generate-queue so queue items
  //      are minted for every assigned agent → they show up in
  //      `GET /dialer/queue` and then in CampaignGroupedQueue.
  //   3. Refresh the campaigns list so the button label flips to
  //      "Pause campaign" immediately.
  //
  // Used by both `draft → start` and `paused → resume` paths so we
  // never end up with an "active" campaign that has no queue items.
  const [starting, setStarting] = useState({}); // { [campaign_id]: true }
  const startCampaign = async (c) => {
    if (starting[c.id]) return;
    if (!c.agent_ids || c.agent_ids.length === 0) {
      toast.error("Assign at least one agent before starting this campaign (Quick Actions → Manage agents).");
      return;
    }
    setStarting(s => ({ ...s, [c.id]: true }));
    try {
      // Step 1 — flip to active
      await axios.patch(
        `${API_BASE}/dialer/campaigns/${c.id}`,
        { status: "active" },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Step 2 — generate queue items so assigned agents see leads
      // immediately. Backend requires either `lead_ids` or `filters` in
      // the body, and defaults assignees to the caller (admin) which is
      // wrong for our use case. We pass the campaign's persisted
      // audience + assigned agents so the queue items land on the right
      // reps, not the admin.
      try {
        const genBody = {};
        const cLeadIds = Array.isArray(c.lead_ids) ? c.lead_ids : [];
        const cFilters = c.filters || null;
        if (cLeadIds.length > 0) genBody.lead_ids = cLeadIds;
        if (cFilters && Object.keys(cFilters).length > 0) {
          // Send a backend-compatible filter subset (the endpoint only
          // honors stages / interest_levels / lead_source / ai_score_min
          // for now; extra keys are ignored which is fine).
          genBody.filters = {
            ...(Array.isArray(cFilters.stages) && cFilters.stages.length ? { stages: cFilters.stages } : {}),
            ...(Array.isArray(cFilters.sources || cFilters.lead_source) && (cFilters.sources || cFilters.lead_source).length
              ? { lead_source: cFilters.sources || cFilters.lead_source } : {}),
            ...(Number.isFinite(cFilters.score_min ?? cFilters.ai_score_min)
              ? { ai_score_min: cFilters.score_min ?? cFilters.ai_score_min } : {}),
          };
          // Drop the key entirely if no honored filter keys made it through.
          if (Object.keys(genBody.filters).length === 0) delete genBody.filters;
        }
        if (Array.isArray(c.agent_ids) && c.agent_ids.length > 0) {
          genBody.assignees = c.agent_ids;
        }
        if (!genBody.lead_ids && !genBody.filters) {
          // Nothing routable — let the user fix it explicitly.
          toast.success("Campaign marked active.");
          toast.error("This campaign has no audience set. Open Edit → Audience to define filters, then try again.");
          onRefreshCampaigns?.();
          return;
        }
        const { data } = await axios.post(
          `${API_BASE}/dialer/campaigns/${c.id}/generate-queue`,
          genBody,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const minted = data?.generated ?? 0;
        const skipped = data?.skipped_existing ?? 0;
        // Iter 276 (Jul 2026) — audience vs. callable delta banner.
        // Answers "why does '6,663 remaining' shrink to '5,021 of
        // 5,021' after Start Calling?" — the difference is DNC +
        // wrong-specialty leads filtered at queue-materialization
        // time (they change independently of the audience snapshot).
        const dncN   = Number(data?.excluded_dnc || 0);
        const wrongN = Number(data?.excluded_wrong_specialty || 0);
        const excluded = dncN + wrongN;
        if (minted > 0) {
          toast.success(`Campaign started — ${minted} lead${minted === 1 ? "" : "s"} routed to ${c.agent_ids.length} agent${c.agent_ids.length === 1 ? "" : "s"}.`);
        } else if (skipped > 0) {
          toast.success(`Campaign started — ${skipped} lead${skipped === 1 ? " was" : "s were"} already queued.`);
        } else {
          toast.success("Campaign started.");
          toast.info("No new leads matched the audience. Use Re-link audience to refresh the lead pool.");
        }
        if (excluded > 0) {
          const parts = [];
          if (dncN)   parts.push(`${dncN.toLocaleString()} DNC`);
          if (wrongN) parts.push(`${wrongN.toLocaleString()} wrong-specialty`);
          toast.info(
            `${excluded.toLocaleString()} lead${excluded === 1 ? "" : "s"} excluded from queue (${parts.join(", ")}) — visible on campaign card as "callable" count.`,
            { duration: 6000 },
          );
        }
      } catch (genErr) {
        const msg = genErr?.response?.data?.detail || "Couldn't generate queue items.";
        toast.success("Campaign marked active.");
        toast.error(`Queue generation: ${msg}`);
      }
      onRefreshCampaigns?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start campaign.");
    } finally {
      setStarting(s => ({ ...s, [c.id]: false }));
    }
  };

  if (!campaigns || campaigns.length === 0) return null;

  // Iter 176 Phase 4 — Effective stats helper. When the rep filter is
  // active, sum {contacted, connected, meetings} for ONLY the selected
  // reps across this campaign. When empty (default), return the cached
  // campaign totals so the existing display stays identical.
  const repFilterArr = Array.from(repFilter);
  const repFilterActive = repFilterArr.length > 0;
  const effectiveStats = (c) => {
    if (!repFilterActive) {
      return {
        contacted: c.contacted ?? 0,
        connected: c.connected ?? 0,
        meetings:  c.meetings ?? 0,
        is_filtered: false,
      };
    }
    const byAgent = perRepStats[c.id] || {};
    let contacted = 0, connected = 0, meetings = 0;
    for (const aid of repFilterArr) {
      const b = byAgent[aid];
      if (!b) continue;
      contacted += b.contacted || 0;
      connected += b.connected || 0;
      meetings  += b.meetings  || 0;
    }
    return { contacted, connected, meetings, is_filtered: true };
  };

  // Iter 176 Phase 4 — Active-reps options for the dropdown. Merge the
  // server's `active_reps` (reps who actually have activity) + the
  // `agent_ids` assigned to ANY visible campaign so admins can pre-
  // filter even before activity exists.
  const repOptionsMap = new Map();
  perRepReps.forEach((r) => repOptionsMap.set(r.id, r));
  campaigns.forEach((c) => (c.agent_ids || []).forEach((aid) => {
    if (repOptionsMap.has(aid)) return;
    const a = agents.find((x) => x.id === aid);
    if (a) repOptionsMap.set(aid, { id: a.id, name: a.name || a.email, email: a.email });
  }));
  const repOptions = Array.from(repOptionsMap.values()).sort(
    (a, b) => (a.name || "").localeCompare(b.name || "")
  );
  const toggleRep = (aid, multi) => {
    setRepFilter((prev) => {
      const next = new Set(multi ? prev : []);
      if (next.has(aid)) next.delete(aid);
      else next.add(aid);
      return next;
    });
  };
  const clearRepFilter = () => setRepFilter(new Set());

  // Iter 175.6 (Danny Tier 2 #7) — status-bucket tabs. Splits campaigns
  // into Active / Completed / Retarget / Archived buckets so completed
  // campaigns don't clutter the active list but stay one click away
  // for the retarget workflow. Default to Active.
  //
  // Iter 178.6 (Danny Feb 2026) — added the Retarget bucket per Danny's
  // reference screenshot. "Retarget" = campaigns where `is_retarget` is
  // true OR where the campaign has a `parent_campaign_id` (the snapshot
  // workflow sets `filters.parent_campaign_id`). We exclude these from
  // the Active bucket so retargets get their own dedicated tab.
  const _isRetarget = (c) => Boolean(
    c?.is_retarget === true
    || c?.parent_campaign_id
    || c?.filters?.parent_campaign_id
  );
  const buckets = {
    active: campaigns.filter(c => ["active", "draft", "paused"].includes(c.status) && !_isRetarget(c)),
    completed: campaigns.filter(c => c.status === "completed"),
    retarget: campaigns.filter(c => _isRetarget(c) && c.status !== "archived"),
    archived: campaigns.filter(c => c.status === "archived"),
  };
  // Iter 178.6 (Danny Feb 2026) — product chip-pill filter (replaces
  // the legacy dropdown). "all" shows every category grouped; selecting
  // a specific category filters to just that one group. The chip set
  // is fixed by PRODUCT_CATEGORIES so admins always see the full
  // taxonomy, not just the products their existing campaigns happen
  // to use (better discoverability for new product lines).
  const visiblePreFilter = buckets[campaignsTab] || buckets.active;
  const creators = Array.from(new Set(
    visiblePreFilter.map(c => c.created_by_name || c.created_by_email || c.created_by).filter(Boolean)
  )).sort();
  const visibleCampaigns = visiblePreFilter.filter(c => {
    if (campaignsCreatorFilter !== "all") {
      const cn = c.created_by_name || c.created_by_email || c.created_by;
      if (cn !== campaignsCreatorFilter) return false;
    }
    if (campaignsProductFilter !== "all") {
      // Iter 178.8 — multi-product campaigns match the chip if ANY
      // of their tagged categories matches the selected filter.
      const codes = categoryCodesForCampaign(c);
      if (!codes.includes(campaignsProductFilter)) return false;
    }
    return true;
  });
  // Group the visible campaigns by product category. Iter 178.8 —
  // multi-product campaigns appear in EVERY group they're tagged
  // with (a CCM+RPM campaign shows under both sections so reps
  // browsing either product can find it). PRODUCT_CATEGORIES order
  // is preserved; OTHER appears only if any campaign lands there.
  const groupedByProduct = (() => {
    const groups = new Map();
    for (const cat of PRODUCT_CATEGORIES) groups.set(cat.code, []);
    groups.set(PRODUCT_OTHER.code, []);
    for (const c of visibleCampaigns) {
      const codes = categoryCodesForCampaign(c);
      for (const code of codes) {
        if (!groups.has(code)) groups.set(code, []);
        groups.get(code).push(c);
      }
    }
    return Array.from(groups.entries())
      .map(([code, list]) => ({ category: getCategory(code), campaigns: list }))
      .filter(g => g.campaigns.length > 0);
  })();
  // KPI strip — totals across the CURRENTLY VISIBLE campaigns. Per
  // Danny's screenshot the cards sit above the status tabs.
  // - Active Campaigns: count of status=active in the visible set
  // - Total Leads: sum of leads across visible campaigns
  // - Meetings Booked: sum of meetings across visible campaigns (rep-filtered if active)
  // - Avg Conversion: meetings ÷ contacted across visible (excluding 0-contacted)
  const kpiStats = (() => {
    let activeN = 0, leadsN = 0, contactedN = 0, meetingsN = 0;
    for (const c of visibleCampaigns) {
      if (c.status === "active") activeN += 1;
      leadsN += c.leads ?? c.total_leads ?? 0;
      const eff = effectiveStats(c);
      contactedN += eff.contacted || 0;
      meetingsN  += eff.meetings  || 0;
    }
    const conv = contactedN > 0 ? (meetingsN / contactedN) * 100 : 0;
    return { activeN, leadsN, meetingsN, convPct: conv };
  })();

  return (
    <div data-testid="admin-campaigns-list" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {/* Iter 178.6 (Danny Feb 2026) — KPI strip per reference screenshot.
          Computed live from the visible campaign set (so flipping
          Active/Completed/Retarget/Archived re-scopes the totals). */}
      <div
        data-testid="admin-campaigns-kpi-strip"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 12,
          marginBottom: 12,
        }}
      >
        {[
          { key: "active-campaigns", label: "ACTIVE CAMPAIGNS", value: kpiStats.activeN.toLocaleString(), tint: "#2563EB" },
          { key: "total-leads",      label: "TOTAL LEADS",      value: kpiStats.leadsN.toLocaleString(),  tint: "#0F172A" },
          { key: "meetings-booked",  label: "MEETINGS BOOKED",  value: kpiStats.meetingsN.toLocaleString(),tint: "#1D9E75" },
          { key: "avg-conversion",   label: "AVG CONVERSION",   value: `${kpiStats.convPct.toFixed(1)}%`,  tint: "#EA8A1A" },
        ].map(card => (
          <div
            key={card.key}
            data-testid={`kpi-${card.key}`}
            style={{
              background: "#fff",
              border: "0.5px solid #e5e7eb",
              borderRadius: 10,
              padding: "14px 16px",
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, letterSpacing: "0.06em" }}>{card.label}</span>
            <span style={{ fontSize: 28, fontWeight: 700, color: card.tint, lineHeight: 1 }}>{card.value}</span>
          </div>
        ))}
      </div>

      {/* Status-bucket tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 4, fontSize: 12, flexWrap: "wrap", alignItems: "center" }}>
        {[
          { id: "active",    label: "Active",    count: buckets.active.length,    testid: "campaigns-tab-active" },
          { id: "completed", label: "Completed", count: buckets.completed.length, testid: "campaigns-tab-completed" },
          { id: "retarget",  label: "Retarget",  count: buckets.retarget.length,  testid: "campaigns-tab-retarget" },
          { id: "archived",  label: "Archived",  count: buckets.archived.length,  testid: "campaigns-tab-archived" },
        ].map(t => {
          const active = campaignsTab === t.id;
          return (
            <button
              key={t.id}
              data-testid={t.testid}
              onClick={() => setCampaignsTab(t.id)}
              style={{
                padding: "5px 12px", borderRadius: 6, fontFamily: "inherit", fontSize: 12,
                cursor: "pointer", fontWeight: active ? 600 : 500,
                background: active ? "#0a2540" : "#fff",
                color: active ? "#fff" : "#374151",
                border: `0.5px solid ${active ? "#0a2540" : "#e5e7eb"}`,
                display: "inline-flex", alignItems: "center", gap: 6,
              }}
            >
              <span>{t.label}</span>
              <span style={{
                padding: "1px 7px", borderRadius: 999, fontSize: 10, fontWeight: 600,
                background: active ? "rgba(255,255,255,0.18)" : "#f1f5f9",
                color: active ? "#fff" : "#64748b",
              }}>{t.count}</span>
            </button>
          );
        })}
        {/* Iter 175.7 — creator filter dropdown (still useful when many
            reps create campaigns). Product filter is now chip-pills
            below (Iter 178.6). */}
        {creators.length > 1 && (
          <>
            <span style={{ marginLeft: 6, color: "#9ca3af", fontSize: 11 }}>·</span>
            <select
              data-testid="campaigns-creator-filter"
              value={campaignsCreatorFilter}
              onChange={(e) => setCampaignsCreatorFilter(e.target.value)}
              style={{ padding: "5px 8px", borderRadius: 6, border: "0.5px solid #e5e7eb", fontSize: 11, fontFamily: "inherit", background: "#fff", color: "#374151" }}
            >
              <option value="all">All creators</option>
              {creators.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {campaignsCreatorFilter !== "all" && (
              <button
                data-testid="campaigns-filter-clear"
                onClick={() => { setCampaignsCreatorFilter("all"); setCampaignsProductFilter("all"); }}
                style={{ padding: "5px 10px", borderRadius: 6, border: "0.5px solid #e5e7eb", fontSize: 11, fontFamily: "inherit", background: "#fff", color: "#dc2626", cursor: "pointer" }}
              >Clear</button>
            )}
          </>
        )}

        {/* Iter 176 Phase 4 (Danny — scoring_and_dialer_updates.md §4)
            — Per-rep filter. Multi-select via shift+click on the rep
            chip. Stats below auto-update via effectiveStats(). */}
        {repOptions.length > 0 && (
          <div style={{ position: "relative", marginLeft: "auto" }} data-testid="campaigns-rep-filter">
            <button
              data-testid="campaigns-rep-filter-toggle"
              onClick={() => setRepFilterOpen((v) => !v)}
              title={repFilterActive ? "Click to change reps; shift+click to toggle each" : "Filter campaign stats by rep"}
              style={{
                padding: "5px 12px", borderRadius: 6,
                fontSize: 11, fontFamily: "inherit",
                background: repFilterActive ? "#0a2540" : "#fff",
                color: repFilterActive ? "#fff" : "#374151",
                border: `0.5px solid ${repFilterActive ? "#0a2540" : "#e5e7eb"}`,
                fontWeight: repFilterActive ? 600 : 500,
                cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
              }}
            >
              <Users style={{ width: 11, height: 11 }} />
              {repFilterActive
                ? (repFilterArr.length === 1
                    ? `Filtered: ${repOptionsMap.get(repFilterArr[0])?.name || "rep"}`
                    : `Filtered: ${repFilterArr.length} reps`)
                : "Filter by rep"}
              {repFilterActive && (
                <span
                  data-testid="campaigns-rep-filter-clear"
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); clearRepFilter(); setRepFilterOpen(false); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); clearRepFilter(); setRepFilterOpen(false); }}}
                  style={{ marginLeft: 4, padding: "0 4px", borderRadius: 4, cursor: "pointer", lineHeight: 1 }}
                >×</span>
              )}
            </button>
            {repFilterOpen && (
              <div
                data-testid="campaigns-rep-filter-menu"
                style={{
                  position: "absolute", top: "calc(100% + 4px)", right: 0,
                  background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 8,
                  padding: 4, minWidth: 220, maxHeight: 320, overflowY: "auto",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)", zIndex: 10,
                }}
              >
                <button
                  data-testid="campaigns-rep-filter-all"
                  onClick={() => { clearRepFilter(); setRepFilterOpen(false); }}
                  style={{
                    width: "100%", textAlign: "left",
                    padding: "6px 10px", fontSize: 11, fontFamily: "inherit",
                    background: !repFilterActive ? "#f3f4f6" : "transparent",
                    border: "none", borderRadius: 6, cursor: "pointer",
                    fontWeight: !repFilterActive ? 600 : 500, color: "#0f172a",
                  }}
                >All reps {!repFilterActive && "✓"}</button>
                <div style={{ borderTop: "0.5px solid #f3f4f6", margin: "4px 0" }} />
                <div style={{ padding: "4px 10px", fontSize: 9, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>
                  Click = pick one · shift+click = multi-select
                </div>
                {repOptions.map((r) => {
                  const selected = repFilter.has(r.id);
                  return (
                    <button
                      key={r.id}
                      data-testid={`campaigns-rep-filter-rep-${r.id}`}
                      data-rep-id={r.id}
                      onClick={(e) => toggleRep(r.id, e.shiftKey)}
                      style={{
                        width: "100%", textAlign: "left",
                        padding: "6px 10px", fontSize: 11, fontFamily: "inherit",
                        background: selected ? "#dbeafe" : "transparent",
                        border: "none", borderRadius: 6, cursor: "pointer",
                        fontWeight: selected ? 600 : 500, color: "#0f172a",
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                      }}
                    >
                      <span>{r.name}</span>
                      {selected && <span style={{ color: "#2563eb" }}>✓</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      {/* Iter 178.6 (Danny Feb 2026) — product chip-pill filter row.
          Replaces the old dropdown. Shows the FULL fixed taxonomy
          (CCM/RPM/AWV/EHR/RCM) so admins can drill in even when their
          existing campaigns don't cover every product. */}
      <div
        data-testid="campaigns-product-chips"
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          margin: "4px 0 14px 0",
          alignItems: "center",
        }}
      >
        <button
          data-testid="campaigns-product-chip-all"
          onClick={() => setCampaignsProductFilter("all")}
          style={{
            padding: "6px 12px",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: campaignsProductFilter === "all" ? 600 : 500,
            background: campaignsProductFilter === "all" ? "#0a2540" : "#fff",
            color: campaignsProductFilter === "all" ? "#fff" : "#374151",
            border: `0.5px solid ${campaignsProductFilter === "all" ? "#0a2540" : "#e5e7eb"}`,
            fontFamily: "inherit",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
          }}
        >
          <span style={{ fontWeight: 700 }}>#</span> All
        </button>
        {PRODUCT_CATEGORIES.map((cat) => {
          const active = campaignsProductFilter === cat.code;
          return (
            <button
              key={cat.code}
              data-testid={`campaigns-product-chip-${cat.code}`}
              onClick={() => setCampaignsProductFilter(active ? "all" : cat.code)}
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: active ? 600 : 500,
                background: active ? cat.color : "#fff",
                color: active ? "#fff" : "#374151",
                border: `0.5px solid ${active ? cat.color : "#e5e7eb"}`,
                fontFamily: "inherit",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 6, height: 6, borderRadius: "50%",
                  background: active ? "#fff" : cat.color,
                  display: "inline-block",
                }}
              />
              {cat.code}
            </button>
          );
        })}
      </div>
      {visibleCampaigns.length === 0 && (
        <div style={{ padding: 24, textAlign: "center", color: "#9ca3af", fontSize: 12, fontStyle: "italic" }}>
          No {campaignsTab} campaigns yet.
          {campaignsTab === "completed" && " Once a campaign&apos;s audience is fully worked, it&apos;ll show up here ready to retarget."}
        </div>
      )}
      {/* Iter 178.6 (Danny Feb 2026) — campaigns grouped by product
          category. Each group has a colored-dot header (matching the
          chip-pill color), the campaign count, the cumulative lead
          total, and a chevron to collapse. Within each group we
          render the campaigns using the same row code as before so
          all existing interactions (expand-to-edit, view leads,
          start, etc.) keep working identically. */}
      {groupedByProduct.map((group) => {
        const cat = group.category;
        const groupLeadTotal = group.campaigns.reduce((acc, c) => acc + (c.leads ?? c.total_leads ?? 0), 0);
        const collapsed = collapsedGroups.has(cat.code);
        return (
          <div
            key={cat.code}
            data-testid={`product-group-${cat.code}`}
            style={{
              background: "#fff",
              borderRadius: 12,
              border: "0.5px solid #e5e7eb",
              padding: "14px 16px",
              marginBottom: 14,
            }}
          >
            <button
              type="button"
              data-testid={`product-group-toggle-${cat.code}`}
              onClick={() => toggleGroup(cat.code)}
              style={{
                width: "100%",
                background: "transparent",
                border: "none",
                padding: 0,
                marginBottom: collapsed ? 0 : 10,
                display: "flex",
                alignItems: "center",
                gap: 10,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 9, height: 9, borderRadius: "50%",
                  background: cat.color, flexShrink: 0,
                }}
              />
              <span style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>{cat.label}</span>
              <span style={{
                padding: "1px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600,
                background: "#f1f5f9", color: "#64748b",
              }}>{group.campaigns.length}</span>
              <span style={{ marginLeft: "auto", fontSize: 11, color: "#94a3b8" }}>
                {groupLeadTotal.toLocaleString()} leads
              </span>
              {collapsed
                ? <ChevronRight style={{ width: 14, height: 14, color: "#94a3b8" }} />
                : <ChevronDown  style={{ width: 14, height: 14, color: "#94a3b8" }} />}
            </button>
            {!collapsed && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {group.campaigns.map(c => {
        const st = STATUS_STYLES[c.status] || STATUS_STYLES.draft;
        const expanded = expandedId === c.id;
        // Iter 176 Phase 4 — effectiveStats applies the per-rep filter
        // to the campaign's contacted/connected/meetings counts.
        const eff = effectiveStats(c);
        const contacted = eff.contacted;
        const total = c.leads ?? c.total_leads ?? 0;
        // Iter 277 (Jul 2026) — prefer callable as the denominator for
        // progress so the bar and the "N / M contacted" line track
        // reality (audience minus DNC + wrong-specialty). Falls back
        // to audience for fresh campaigns that haven't been Start-
        // Called yet.
        const ds          = c.dialable_summary;
        const hasCallable = !!ds;
        const dialableN   = ds ? ds.dialable : 0;
        const denom       = hasCallable ? contacted + dialableN : total;
        const progress    = denom > 0 ? Math.min(100, (contacted / denom) * 100) : 0;
        const filters = c.filters || {};
        const filterChips = [
          ...(filters.stages || []).map(v => ({ type: "Stage", value: v, bg: "#E6F1FB", color: "#185FA5" })),
          ...(filters.ai_score_min ? [{ type: "AI Score", value: `≥ ${filters.ai_score_min}`, bg: "#FAEEDA", color: "#854F0B" }] : []),
          ...(filters.product_type || []).map(v => ({ type: "Product", value: v, bg: "#F3E8FF", color: "#6B21A8" })),
          ...(filters.geography || []).map(v => ({ type: "Geo", value: v, bg: "#DCFCE7", color: "#166534" })),
          ...(filters.lead_source || []).map(v => ({ type: "Source", value: v, bg: "#E6F1FB", color: "#185FA5" })),
        ];
        const script = scripts.find(s => s.id === c.script_id);
        const campaignAgents = (c.agent_ids || []).map(id => agents.find(a => a.id === id)).filter(Boolean);
        return (
          <div key={c.id} data-testid={`campaign-row-${c.id}`}
            style={{
              background: "#fff",
              borderRadius: 10,
              borderTop: expanded ? "1.5px solid #2563eb" : "0.5px solid #eee",
              borderRight: expanded ? "1.5px solid #2563eb" : "0.5px solid #eee",
              borderBottom: expanded ? "1.5px solid #2563eb" : "0.5px solid #eee",
              borderLeft: `4px solid ${st.border}`,
              overflow: "hidden",
            }}>
            <button
              data-testid={`campaign-row-toggle-${c.id}`}
              onClick={() => toggle(c.id)}
              style={{ width: "100%", background: "transparent", border: "none", padding: "14px 18px", display: "flex", alignItems: "center", gap: 14, cursor: "pointer", fontFamily: "var(--font)", textAlign: "left" }}>
              {expanded ? <ChevronDown style={{ width: 14, height: 14, color: "#888" }} /> : <ChevronRight style={{ width: 14, height: 14, color: "#888" }} />}
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{c.name}</span>
                  <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 10, background: st.bg, color: st.color, fontWeight: 500, textTransform: "capitalize" }}>{c.status}</span>
                  <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 10, background: "#EEEDFE", color: "#3C3489" }}>{c.type}</span>
                  {/* Wave 2 / A8 — agent count badge on the collapsed row. */}
                  {campaignAgents.length > 0 && (
                    <span
                      data-testid={`campaign-agent-count-${c.id}`}
                      title={campaignAgents.map(a => a.name).join(", ")}
                      style={{ padding: "2px 8px", borderRadius: 10, fontSize: 10, background: "#E6F1FB", color: "#185FA5", fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 4 }}
                    >
                      <Users style={{ width: 9, height: 9 }} /> {campaignAgents.length}
                    </span>
                  )}
                  {/* Iter 178.8 (Josh Feb 2026) — inline product-tag
                      editor. Renders a chip per assigned product +
                      a "+" chip that opens a small picker for the
                      remaining categories. Clicks must NOT bubble
                      up to the row toggle. */}
                  {(() => {
                    const assignedCodes = categoryCodesForCampaign(c).filter(code => code !== PRODUCT_OTHER.code);
                    const picker = tagMenuFor === c.id;
                    return (
                      <span data-testid={`campaign-tags-${c.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, position: "relative" }}>
                        {assignedCodes.map(code => {
                          const cat = getCategory(code);
                          return (
                            <button
                              key={code}
                              type="button"
                              data-testid={`campaign-tag-${c.id}-${code}`}
                              onClick={(e) => { e.stopPropagation(); toggleProductTag(c, code); }}
                              title={`Remove ${cat.label}`}
                              style={{
                                padding: "2px 8px 2px 7px",
                                borderRadius: 999,
                                fontSize: 10,
                                fontWeight: 600,
                                background: cat.color,
                                color: "#fff",
                                border: "none",
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 5,
                                fontFamily: "inherit",
                                lineHeight: 1.3,
                              }}
                            >
                              <span aria-hidden style={{ width: 5, height: 5, borderRadius: "50%", background: "#fff", opacity: 0.85 }} />
                              {code}
                              <X style={{ width: 9, height: 9, opacity: 0.85 }} />
                            </button>
                          );
                        })}
                        <button
                          type="button"
                          data-testid={`campaign-tag-add-${c.id}`}
                          onClick={(e) => { e.stopPropagation(); setTagMenuFor(picker ? null : c.id); }}
                          style={{
                            padding: "2px 7px",
                            borderRadius: 999,
                            fontSize: 10,
                            fontWeight: 600,
                            background: picker ? "#0a2540" : "#f8fafc",
                            color: picker ? "#fff" : "#64748b",
                            border: `0.5px dashed ${picker ? "#0a2540" : "#cbd5e1"}`,
                            cursor: "pointer",
                            fontFamily: "inherit",
                            lineHeight: 1.3,
                          }}
                          title="Tag product"
                        >
                          {assignedCodes.length === 0 ? "+ tag product" : "+"}
                        </button>
                        {picker && (
                          <div
                            data-testid={`campaign-tag-picker-${c.id}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              position: "absolute",
                              top: "100%",
                              left: 0,
                              marginTop: 6,
                              background: "#fff",
                              border: "0.5px solid #e5e7eb",
                              borderRadius: 10,
                              padding: 6,
                              boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
                              zIndex: 30,
                              minWidth: 180,
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            {PRODUCT_CATEGORIES.map(cat => {
                              const isOn = assignedCodes.includes(cat.code);
                              return (
                                <button
                                  key={cat.code}
                                  type="button"
                                  data-testid={`campaign-tag-pick-${c.id}-${cat.code}`}
                                  onClick={(e) => { e.stopPropagation(); toggleProductTag(c, cat.code); setTagMenuFor(null); }}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    padding: "6px 8px",
                                    background: isOn ? "#f1f5f9" : "transparent",
                                    border: "none",
                                    borderRadius: 6,
                                    cursor: "pointer",
                                    fontFamily: "inherit",
                                    fontSize: 11,
                                    textAlign: "left",
                                    color: "#0f172a",
                                  }}
                                >
                                  <span aria-hidden style={{ width: 8, height: 8, borderRadius: "50%", background: cat.color }} />
                                  <span style={{ flex: 1 }}>{cat.label}</span>
                                  {isOn && <Check style={{ width: 11, height: 11, color: "#1D9E75" }} />}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </span>
                    );
                  })()}
                </div>
                {(c.filters?.parent_campaign_id || c.parent_campaign_id) && (
                  <div data-testid={`campaign-retarget-of-${c.id}`} style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>
                    ↺ Retarget of {(campaigns.find((x) => x.id === (c.filters?.parent_campaign_id || c.parent_campaign_id))?.name) || "parent campaign"}
                  </div>
                )}
                <div style={{ display: "flex", gap: 16, fontSize: 11, color: "#888", flexWrap: "wrap" }}>
                  {/* Iter 277 (Jul 2026) — Hero "to dial" number.
                      Displays the callable count (audience minus DNC + wrong-
                      specialty) when the campaign has been Start-Called at
                      least once. Falls back to audience-adjusted count with a
                      "will filter on start" hint for fresh campaigns.

                      Prioritizing callable over audience answers the exact
                      confusion the rep had — "6,663 remaining to call" was
                      hiding that only ~5,021 leads are actually dialable. */}
                  {(() => {
                    // Live dialable count — same rule the dialer queue uses (current stage, DNC).
                    const ds = c.dialable_summary;
                    if (ds) {
                      const brk = Object.entries(ds.not_dialable_breakdown || {}).map(([k, v]) => `${v.toLocaleString()} ${k}`).join(" · ");
                      const reps = (ds.by_rep || []).map((r) => `  · ${r.name}: ${r.dialable.toLocaleString()}`).join("\n");
                      return (
                        <span data-testid={`campaign-to-dial-hero-${c.id}`} style={{ display: "inline-flex", alignItems: "baseline", gap: 8, marginRight: 12 }}>
                          <span data-testid={`campaign-remaining-${c.id}`} title={reps ? `Dialable per rep:\n${reps}` : undefined}
                            style={{ fontSize: 16, fontWeight: 700, color: "#0F766E", letterSpacing: "-0.01em", cursor: reps ? "help" : undefined }}>
                            {ds.dialable.toLocaleString()}
                          </span>
                          <span style={{ fontSize: 11, color: "#0F766E", fontWeight: 500 }}>dialable</span>
                          <span data-testid={`campaign-audience-${c.id}`} title={brk ? `Not dialable right now:\n  · ${brk.split(" · ").join("\n  · ")}` : undefined}
                            style={{ fontSize: 10, color: "#94A3B8", cursor: brk ? "help" : undefined }}>
                            of {total.toLocaleString()} audience{ds.not_dialable > 0 && (
                              <> · <span data-testid={`campaign-not-dialable-${c.id}`} style={{ color: "#B45309", fontWeight: 600 }}>{brk}</span></>
                            )}
                          </span>
                        </span>
                      );
                    }
                    return total > 0 ? (
                      <span data-testid={`campaign-to-dial-hero-${c.id}`} style={{ display: "inline-flex", alignItems: "baseline", gap: 8, marginRight: 12 }}>
                        <span data-testid={`campaign-audience-${c.id}`} title="The dialable count appears after the first Start Calling." style={{ fontSize: 11, color: "#334155", cursor: "help" }}>
                          {total.toLocaleString()} audience · dialable count appears after Start Calling
                        </span>
                      </span>
                    ) : null;
                  })()}

                  <span>{contacted} contacted</span>
                  <span>{eff.connected} connected</span>
                  <span style={{ fontWeight: 600, color: "#1D9E75" }}>{eff.meetings} meetings</span>
                  {/* Wave 2 / A8 — connect rate at-a-glance. */}
                  {contacted > 0 && (
                    <span
                      data-testid={`campaign-connect-rate-${c.id}`}
                      style={{ fontWeight: 600, color: "#185FA5" }}
                    >
                      {Math.round(100 * eff.connected / contacted)}% connect
                    </span>
                  )}
                  {/* Iter 176 Phase 4 — visual marker when rep filter is active. */}
                  {eff.is_filtered && (
                    <span
                      data-testid={`campaign-rep-filtered-${c.id}`}
                      title={`Stats shown for ${repFilterArr.length} selected rep(s) only`}
                      style={{ padding: "1px 6px", borderRadius: 8, background: "#dbeafe", color: "#1d4ed8", fontWeight: 600, fontSize: 10 }}
                    >
                      rep-filtered
                    </span>
                  )}
                  {/* Wave 2 / A8 — created date + by. Both fields are
                      best-effort: backend may persist as ISO string or
                      embed creator email in `created_by_name`. */}
                  {c.created_at && (
                    <span data-testid={`campaign-created-at-${c.id}`}>
                      created {new Date(c.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                  )}
                  {(c.created_by_name || c.created_by_email || c.created_by) && (
                    <span data-testid={`campaign-created-by-${c.id}`}>
                      by {c.created_by_name || c.created_by_email || c.created_by}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#2563eb" }}>{c.conversion || "—"}</div>
                <div style={{ fontSize: 10, color: "#888" }}>Conversion</div>
              </div>
            </button>

            {expanded && (
              <div data-testid={`campaign-row-detail-${c.id}`} style={{ borderTop: "0.5px solid #eee", display: "flex", alignItems: "stretch" }}>
                {/* Left column — 4 sections */}
                <div style={{ flex: 1, padding: "14px 16px", borderRight: "0.5px solid #eee" }}>
                  {/* Section 1 — Stats grid + progress */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 14 }}>
                    {[
                      // Iter 277 (Jul 2026) — headline metric is
                      // "Callable" when known; audience shown as sub-
                      // caption so the rep still sees the original
                      // pool size. Falls back to "Total leads" for
                      // fresh campaigns.
                      hasCallable
                        ? { label: "Dialable leads", val: dialableN, sub: `of ${total.toLocaleString()} audience`, color: "#0F766E" }
                        : { label: "Total leads",    val: total,      sub: null,                                    color: "#2563eb" },
                      { label: "Contacted", val: contacted, color: "#0071e3" },
                      { label: "Connected", val: eff.connected, color: "#A78BFA" },
                      { label: "Meetings", val: eff.meetings, color: "#1D9E75" },
                    ].map(m => (
                      <div key={m.label} style={{ background: "#fafafa", padding: "8px 10px", borderRadius: 6, border: "0.5px solid #eee" }}>
                        <div style={{ fontSize: 9, color: "#888", textTransform: "uppercase", letterSpacing: "0.3px" }}>{m.label}</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: m.color, marginTop: 1 }}>
                          {typeof m.val === "number" ? m.val.toLocaleString() : m.val}
                        </div>
                        {m.sub && <div style={{ fontSize: 9, color: "#94A3B8", marginTop: 2 }}>{m.sub}</div>}
                      </div>
                    ))}
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 10, color: "#888", marginBottom: 4 }}>
                      Campaign progress: <strong>{contacted.toLocaleString()} / {denom.toLocaleString()}</strong>
                      {hasCallable ? " (contacted + dialable)" : " total"} contacted
                      {hasCallable && total !== denom && (
                        <span style={{ marginLeft: 6, color: "#94A3B8" }}>
                          (audience: {total.toLocaleString()})
                        </span>
                      )}
                    </div>
                    <div style={{ height: 6, background: "#eee", borderRadius: 3, overflow: "hidden" }}>
                      <div data-testid={`campaign-progress-${c.id}`} style={{ height: 6, width: `${progress}%`, background: hasCallable ? "#0F766E" : "#2563eb", borderRadius: 3, transition: "width 0.3s" }} />
                    </div>
                  </div>

                  {/* Section 2 — Lead filters */}
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
                      <span style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", flex: 1 }}>Lead filters</span>
                      <button data-testid={`campaign-edit-filters-${c.id}`} onClick={() => onOpenEdit?.(c, 2)} style={{ fontSize: 11, color: "#185FA5", background: "none", border: "none", cursor: "pointer", fontFamily: "var(--font)" }}>Edit filters →</button>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {filterChips.length === 0 && <span style={{ fontSize: 11, color: "#888" }}>No filters configured.</span>}
                      {filterChips.map((f, i) => (
                        <span key={i} data-testid={`filter-chip-${c.id}-${i}`} style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: f.bg, color: f.color, fontWeight: 500 }}>
                          {f.type}: {f.value}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Section 3 — Assigned script */}
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
                      <span style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", flex: 1 }}>Assigned script</span>
                      <button data-testid={`campaign-change-script-${c.id}`} onClick={() => onOpenEdit?.(c, 3)} style={{ fontSize: 11, color: "#185FA5", background: "none", border: "none", cursor: "pointer", fontFamily: "var(--font)" }}>Change script →</button>
                    </div>
                    {script ? (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, padding: "4px 10px", borderRadius: 12, background: "#E6F1FB", color: "#185FA5", fontWeight: 500 }}>
                        <FileText style={{ width: 11, height: 11 }} /> {script.name}
                        {script.sections ? <span style={{ fontSize: 10, opacity: 0.7 }}>· {script.sections.length} sections</span> : null}
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, color: "#888" }}>No script assigned.</span>
                    )}
                  </div>

                  {/* Section 4 — Assigned agents */}
                  <div>
                    <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
                      <span style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", flex: 1 }}>Assigned agents</span>
                      <button data-testid={`campaign-manage-agents-${c.id}`} onClick={() => onOpenEdit?.(c, 4)} style={{ fontSize: 11, color: "#185FA5", background: "none", border: "none", cursor: "pointer", fontFamily: "var(--font)" }}>Manage agents →</button>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {campaignAgents.length === 0 && <span style={{ fontSize: 11, color: "#888" }}>No agents assigned.</span>}
                      {campaignAgents.map(a => (
                        <span key={a.id} data-testid={`campaign-agent-pill-${c.id}-${a.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, padding: "3px 8px", borderRadius: 12, background: "#fff", border: "0.5px solid #ddd" }}>
                          <span style={{ width: 18, height: 18, borderRadius: "50%", background: "#E6F1FB", color: "#185FA5", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700 }}>
                            {(a.name || "").split(" ").map(n => n[0]).join("").slice(0, 2)}
                          </span>
                          {a.name} <span style={{ fontSize: 9, color: "#888" }}>· {a.callsToday ?? 0}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right column — quick actions + daily perf */}
                <div style={{ width: 260, flexShrink: 0, background: "#fafafa", padding: "14px 16px" }}>
                  <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 8 }}>Quick actions</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 14 }}>
                    {[
                      { id: "retarget", label: "↺ Duplicate & retarget", icon: RotateCcw, onClick: () => setRetargetCampaign(c) },
                      // Iter 158 (Josh) — was "Start all agents" but it only flipped
                      // status=active without showing anything. Now opens the agent roster.
                      { id: "agents_view", label: "View agents", icon: Users, primary: true, onClick: () => openAgentsLightbox(c) },
                      { id: "leads", label: "View leads", icon: Users, onClick: () => openLeadsLightbox(c) },
                      { id: "edit", label: "Edit campaign", icon: Pencil, onClick: () => onOpenEdit?.(c, 1) },
                      { id: "script", label: "Change script", icon: FileText, onClick: () => onOpenEdit?.(c, 3) },
                      { id: "agents", label: "Manage agents", icon: Users, onClick: () => onOpenEdit?.(c, 4) },
                      // Iter 158 (Josh) — Re-link audience: rebuilds lead_ids from filters.
                      { id: "snapshot", label: snapshotting[c.id] ? "Linking…" : "Re-link audience", icon: RotateCcw, onClick: () => snapshotAudience(c) },
                      { id: "analytics", label: "View analytics", icon: BarChart3, onClick: () => onSwitchToAnalytics?.(c.id) },
                      // Iter 165 (Danny report fix) — context-aware
                      // primary action: draft → "Start campaign" (also
                      // generates queue items); active → "Pause"; any
                      // other state → "Resume" (also generates queue
                      // items so the campaign shows up under assigned
                      // agents). Marked `primary` so it visually pops
                      // — this is THE button on this card.
                      (c.status === "active"
                        ? { id: "toggle", label: "Pause campaign", icon: Pause,
                            onClick: () => patchCampaign(c.id, { status: "paused" }, "Campaign paused") }
                        : c.status === "completed"
                        ? { id: "retarget", label: starting[c.id] ? "Retargeting…" : "Retarget", icon: Play,
                            primary: true,
                            // Iter 175.6 — completed campaigns get a
                            // Retarget primary action that spawns a new
                            // campaign with the voicemail/no-answer
                            // leads (excluding DNC + wrong-number).
                            onClick: async () => {
                              setStarting((s) => ({ ...s, [c.id]: true }));
                              try {
                                const r = await axios.post(
                                  `${process.env.REACT_APP_BACKEND_URL}/api/dialer/campaigns/${c.id}/retarget`,
                                  {},
                                  { headers: { Authorization: `Bearer ${token}` } },
                                );
                                toast.success(`Retarget created: ${r.data.audience_size} leads (excluded ${r.data.excluded_dnc} DNC)`);
                                onRefreshCampaigns?.();
                              } catch (e) {
                                const d = e?.response?.data?.detail;
                                toast.error(typeof d === "object" ? d?.message : (d || "Retarget failed"));
                              } finally {
                                setStarting((s) => ({ ...s, [c.id]: false }));
                              }
                            } }
                        : { id: "toggle",
                            label: starting[c.id]
                              ? (c.status === "draft" ? "Starting…" : "Resuming…")
                              : (c.status === "draft" ? "Start campaign" : "Resume campaign"),
                            icon: Play,
                            primary: true,
                            onClick: () => startCampaign(c) }
                      ),
                      { id: "archive", label: "Archive", icon: Archive, danger: true, onClick: () => patchCampaign(c.id, { status: "archived" }, "Campaign archived") },
                    ].map(a => (
                      <button key={a.id} data-testid={`campaign-quick-action-${c.id}-${a.id}`} onClick={a.onClick}
                        style={{
                          display: "flex", alignItems: "center", gap: 5, padding: "7px 10px",
                          border: a.danger ? "0.5px solid #F7C1C1" : "0.5px solid #eee",
                          borderRadius: 8, fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: "var(--font)",
                          background: a.primary ? "#2563eb" : "#fff",
                          color: a.primary ? "#fff" : a.danger ? "#A32D2D" : "var(--text-primary)",
                          textAlign: "left",
                        }}>
                        <a.icon style={{ width: 11, height: 11 }} /> {a.label}
                      </button>
                    ))}
                  </div>

                  {/* Top-performing agent */}
                  <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 6 }}>Top performer</div>
                  {campaignAgents.length > 0 ? (
                    (() => {
                      const top = [...campaignAgents].sort((a, b) => (b.meetings ?? 0) - (a.meetings ?? 0))[0];
                      return (
                        <div data-testid={`campaign-top-agent-${c.id}`} style={{ background: "#fff", border: "0.5px solid #eee", borderRadius: 8, padding: "8px 10px", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ width: 28, height: 28, borderRadius: "50%", background: "#E6F1FB", color: "#185FA5", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>
                            {(top.name || "").split(" ").map(n => n[0]).join("").slice(0, 2)}
                          </span>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 500 }}>{top.name}</div>
                            <div style={{ fontSize: 10, color: "#888" }}>
                              {top.callsToday ?? 0} calls · {top.meetings ?? 0} meetings · {top.connected && top.callsToday ? `${Math.round(100 * top.connected / top.callsToday)}%` : "—"}
                            </div>
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    <div style={{ fontSize: 11, color: "#888", marginBottom: 12 }}>No agents assigned yet.</div>
                  )}

                  {/* Daily target */}
                  <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>Daily call target</div>
                  <div style={{ fontSize: 20, fontWeight: 500, color: "#2563eb", marginTop: 2 }}>
                    {(campaignAgents.reduce((s, a) => s + (a.callsToday ?? 0), 0))} <span style={{ fontSize: 12, color: "#888", fontWeight: 400 }}>/ {(c.daily_target_per_agent ?? 100) * Math.max(campaignAgents.length, 1)}</span>
                  </div>
                  <div style={{ height: 6, background: "#eee", borderRadius: 3, marginTop: 4, overflow: "hidden" }}>
                    <div style={{ height: 6, width: `${Math.min(100, (campaignAgents.reduce((s, a) => s + (a.callsToday ?? 0), 0) / Math.max((c.daily_target_per_agent ?? 100) * campaignAgents.length, 1)) * 100)}%`, background: "#2563eb", borderRadius: 3 }} />
                  </div>
                  <div style={{ fontSize: 10, color: "#888", marginTop: 3 }}>Team total today</div>
                </div>
              </div>
            )}
          </div>
        );
                })}
              </div>
            )}
          </div>
        );
      })}
      {/* §4 Duplicate & Retarget modal */}
      <RetargetCampaignModal
        open={!!retargetCampaign}
        parentCampaign={retargetCampaign}
        parentLeadCount={(retargetCampaign && retargetCampaign.total_leads) || 1200}
        scripts={scripts || []}
        agents={agents || []}
        existingRetargetCount={(campaigns || []).filter((x) => /Retarget R\d+/i.test(x?.name || "")).length}
        onCancel={() => setRetargetCampaign(null)}
        onSaveDraft={async (payload) => {
          try {
            await axios.post(`${API_BASE}/dialer/campaigns`, {
              name: payload.name,
              type: "retarget",
              status: "draft",
              priority: "normal",
              script_id: payload.script_id || undefined,
              agent_ids: payload.agent_ids || [],
              daily_target_per_agent: payload.daily_target_per_agent,
              filters: { parent_campaign_id: payload.parent_campaign_id, audience: payload.audience, advanced: payload.advanced, cadence: payload.cadence, wait_period: payload.wait_period },
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(`Retarget campaign saved as draft — ${payload.target_count} leads`);
            onRefreshCampaigns?.();
          } catch (_e) { toast.error("Failed to save draft"); }
          setRetargetCampaign(null);
        }}
        onLaunch={async (payload) => {
          try {
            await axios.post(`${API_BASE}/dialer/campaigns`, {
              name: payload.name,
              type: "retarget",
              status: "active",
              priority: "normal",
              script_id: payload.script_id || undefined,
              agent_ids: payload.agent_ids || [],
              daily_target_per_agent: payload.daily_target_per_agent,
              filters: { parent_campaign_id: payload.parent_campaign_id, audience: payload.audience, advanced: payload.advanced, cadence: payload.cadence, wait_period: payload.wait_period },
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(`Retarget campaign launched — ${payload.target_count} leads added to ${payload.name} ✓`);
            onRefreshCampaigns?.();
          } catch (_e) { toast.error("Failed to launch retarget campaign"); }
          setRetargetCampaign(null);
        }}
      />
      {/* Iter 142 — View leads lightbox (in-place) */}
      {leadsLightbox && (
        <CampaignLeadsLightbox
          state={leadsLightbox}
          onClose={() => setLeadsLightbox(null)}
          onEdit={(c) => onOpenEdit?.(c, 2)}
        />
      )}
      {/* Iter 158 (Josh) — Agents roster lightbox (in-place) */}
      {agentsLightbox && (
        <CampaignAgentsLightbox
          state={agentsLightbox}
          onClose={() => setAgentsLightbox(null)}
          onActivate={async () => {
            await patchCampaign(agentsLightbox.campaign.id, { status: "active" }, "Campaign activated");
            setAgentsLightbox(null);
          }}
          onManage={() => { onOpenEdit?.(agentsLightbox.campaign, 4); setAgentsLightbox(null); }}
        />
      )}
    </div>
  );
}

/* ── Iter 158 (Josh) — Campaign agents lightbox ────────────────────────
   Replaces the old "Start all agents" button (which silently flipped
   status=active). Now opens a roster panel showing every assigned
   agent's status, calls-today, and per-row reassign action. Includes a
   primary "Activate campaign" CTA that performs the same status flip
   the old button did — but only after the admin sees who's affected. */
function CampaignAgentsLightbox({ state, onClose, onActivate, onManage }) {
  const { campaign, agents = [] } = state;
  const isActive = campaign?.status === "active";
  const summary = useMemo(() => ({
    total: agents.length,
    on_call: agents.filter(a => a.status === "on_call").length,
    available: agents.filter(a => a.status === "available").length,
    wrap_up: agents.filter(a => a.status === "wrap_up").length,
    on_break: agents.filter(a => a.status === "break").length,
  }), [agents]);
  return (
    <div data-testid="campaign-agents-lightbox" onClick={(e) => e.target === e.currentTarget && onClose()} style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(15,23,42,0.55)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 12, width: "100%", maxWidth: 720, maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden", fontFamily: "var(--font, system-ui)" }}>
        <div style={{ padding: "14px 20px", borderBottom: "0.5px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>Campaign · agents</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginTop: 2 }}>{campaign?.name || "Campaign"}</div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
              {summary.total} assigned · {summary.on_call} on call · {summary.available} available · {summary.wrap_up} wrap-up · {summary.on_break} break
            </div>
          </div>
          <button data-testid="campaign-agents-lightbox-close" onClick={onClose} style={{ padding: 6, background: "transparent", border: 0, cursor: "pointer", color: "#64748b" }}>
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>
        <div style={{ flex: 1, overflow: "auto" }}>
          {agents.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>
              No agents assigned yet.{" "}
              <button data-testid="campaign-agents-lightbox-manage-empty" onClick={onManage} style={{ background: "none", border: 0, color: "#185FA5", cursor: "pointer", fontFamily: "inherit", fontSize: 12, textDecoration: "underline" }}>
                Assign agents →
              </button>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#f8fafc", color: "#475569", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {["Agent", "Role", "Status", "Calls today"].map((h) => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left", borderBottom: "0.5px solid #e2e8f0" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {agents.map((a, i) => {
                  const st = AGENT_TILE_STYLE[a.status] || { border: "#888", bg: "#f5f5f5" };
                  return (
                    <tr key={a.id || i} data-testid={`campaign-agents-lightbox-row-${a.id || i}`} style={{ borderTop: "0.5px solid #f1f5f9" }}>
                      <td style={{ padding: "8px 14px", color: "#0f172a", fontWeight: 500 }}>{a.name}</td>
                      <td style={{ padding: "8px 14px", color: "#475569" }}>{a.role || "—"}</td>
                      <td style={{ padding: "8px 14px" }}>
                        <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 10, background: st.bg, color: st.border, fontWeight: 500 }}>{a.status || "—"}</span>
                      </td>
                      <td style={{ padding: "8px 14px", color: "#475569", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{a.callsToday ?? 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ padding: "12px 20px", borderTop: "0.5px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button data-testid="campaign-agents-lightbox-manage" onClick={onManage} className="btn" style={{ fontSize: 12 }}>Manage agents</button>
          {!isActive && (
            <button data-testid="campaign-agents-lightbox-activate" onClick={onActivate} className="btn btn-primary" style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5 }}>
              <Play style={{ width: 12, height: 12 }} /> Activate campaign
            </button>
          )}
          {isActive && (
            <span data-testid="campaign-agents-lightbox-active-pill" style={{ fontSize: 11, color: "#1D9E75", padding: "6px 10px", background: "#DAFBE8", borderRadius: 8, fontWeight: 600 }}>Campaign active ✓</span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Iter 142 — Campaign leads lightbox (Iter 166 — empty-audience hint) ─ */
function CampaignLeadsLightbox({ state, onClose, onSnapshot, onEdit }) {
  const { campaign, loading, items = [], total = 0, error, source } = state;
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter((l) =>
      ((l.company || l.name || "") + " " + (l.specialty || "") + " " + (l.state || "") + " " + (l.stage || ""))
      .toLowerCase().includes(q)
    );
  }, [items, query]);
  const fmtUsd = (v) => v ? `$${Number(v).toLocaleString()}` : "—";
  // Iter 166 (Josh — issue #6 peter@ehiehr.com): a campaign with no
  // `lead_ids` AND no `filters` is "audienceless" — created without filter
  // selections (likely a stray double-click in the create wizard). The old
  // empty state ("No leads in this campaign's audience.") looked like a
  // load failure. We now distinguish "audience never set" from "filters set
  // but no leads matched" and give the user a recovery action.
  const hasFilters = useMemo(() => {
    const f = campaign?.filters || {};
    if (!f || typeof f !== "object") return false;
    // Treat empty arrays / falsy values as "no filter"
    return Object.values(f).some(v =>
      (Array.isArray(v) && v.length > 0) ||
      (typeof v === "number" && v > 0) ||
      (typeof v === "string" && v.trim() !== "")
    );
  }, [campaign]);
  const audienceless = !loading && !error && filtered.length === 0 && !campaign?.lead_ids?.length && !hasFilters;
  return (
    <div data-testid="campaign-leads-lightbox" onClick={(e) => e.target === e.currentTarget && onClose()} style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(15,23,42,0.55)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 12, width: "100%", maxWidth: 1000, maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden", fontFamily: "var(--font, system-ui)" }}>
        <div style={{ padding: "14px 20px", borderBottom: "0.5px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>Campaign · audience</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginTop: 2 }}>{campaign?.name || "Campaign"}</div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
              {loading ? "Loading…" : `${total} lead${total === 1 ? "" : "s"}${source ? ` · from ${source}` : ""}`}
            </div>
          </div>
          <button data-testid="campaign-leads-lightbox-close" onClick={onClose} style={{ padding: 6, background: "transparent", border: 0, cursor: "pointer", color: "#64748b" }}>
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>
        <div style={{ padding: "10px 20px", borderBottom: "0.5px solid #f1f5f9" }}>
          <input
            data-testid="campaign-leads-lightbox-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by name, specialty, state, or stage…"
            style={{ width: "100%", padding: "7px 10px", fontSize: 12, fontFamily: "inherit", border: "0.5px solid #e2e8f0", borderRadius: 8, outline: "none", boxSizing: "border-box" }}
          />
        </div>
        <div style={{ flex: 1, overflow: "auto" }}>
          {error && <div style={{ padding: 24, color: "#991b1b", fontSize: 12 }}>{error}</div>}
          {!error && (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#f8fafc", color: "#475569", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {["Company", "Specialty", "State", "Stage", "Value", "Phone"].map((h) => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left", borderBottom: "0.5px solid #e2e8f0" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>Loading…</td></tr>
                ) : filtered.length === 0 ? (
                  audienceless ? (
                    <tr><td colSpan={6} style={{ padding: 36, textAlign: "center" }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", marginBottom: 6 }}>This campaign has no audience yet</div>
                      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 14, lineHeight: 1.5, maxWidth: 460, margin: "0 auto 14px" }}>
                        No filter was saved when this campaign was created, so there are no leads to dial. Open the campaign settings to set filters (state, specialty, beneficiaries, etc.) and snapshot an audience.
                      </div>
                      {onEdit && (
                        <button
                          data-testid="campaign-leads-lightbox-edit-filters"
                          onClick={() => { onEdit(campaign); onClose?.(); }}
                          className="btn btn-primary"
                          style={{ fontSize: 12, padding: "8px 14px" }}
                        >
                          Edit campaign filters
                        </button>
                      )}
                    </td></tr>
                  ) : (
                    <tr><td colSpan={6} style={{ padding: 32, textAlign: "center", color: "#94a3b8", fontStyle: "italic" }}>No leads in this campaign's audience.</td></tr>
                  )
                ) : filtered.map((l, i) => (
                  <tr key={l.id || i} data-testid={`campaign-leads-lightbox-row-${i}`} style={{ borderTop: "0.5px solid #f1f5f9" }}>
                    <td style={{ padding: "8px 14px", color: "#0f172a" }}>
                      {l.id ? <a href={`/lead-focus/${l.id}`} target="_blank" rel="noreferrer" style={{ color: "#5B6CFF", textDecoration: "none", fontWeight: 600 }}>{l.company || l.name || "(no name)"}</a> : (l.company || l.name)}
                    </td>
                    <td style={{ padding: "8px 14px", color: "#475569" }}>{l.specialty || "—"}</td>
                    <td style={{ padding: "8px 14px", color: "#475569" }}>{l.state || "—"}</td>
                    <td style={{ padding: "8px 14px", color: "#475569" }}>{l.stage || "—"}</td>
                    <td style={{ padding: "8px 14px", color: "#475569", fontVariantNumeric: "tabular-nums" }}>{fmtUsd(l.value)}</td>
                    <td style={{ padding: "8px 14px", color: "#475569", fontFamily: "ui-monospace, monospace" }}>{l.phone || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
const AGENT_TILE_STYLE = {
  on_call:   { border: "#E24B4A", bg: "#FCEBEB" },
  available: { border: "#1D9E75", bg: "#EAF3DE" },
  wrap_up:   { border: "#BA7517", bg: "#FAEEDA" },
  break:     { border: "#888",    bg: "#f5f5f5" },
};
export function AdminAgentsPanel({
  agents, campaigns, token, onOpenInvite, onRefresh,
}) {
  const [detailAgent, setDetailAgent] = useState(null);
  const [reassignFor, setReassignFor] = useState(null);
  const [reassignTarget, setReassignTarget] = useState("");

  const doReassign = async () => {
    if (!reassignTarget) return;
    try {
      await axios.post(
        `${API_BASE}/dialer/agents/${reassignFor.id}/reassign`,
        { campaign_id: reassignTarget, move_open_items: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`${reassignFor.name} reassigned`);
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reassign failed");
    } finally {
      setReassignFor(null);
      setReassignTarget("");
    }
  };

  const tiles = [
    { id: "on_call", label: "On call", val: agents.filter(a => a.status === "on_call").length },
    { id: "available", label: "Available", val: agents.filter(a => a.status === "available").length },
    { id: "wrap_up", label: "Wrap-up", val: agents.filter(a => a.status === "wrap_up").length },
    { id: "break", label: "Break", val: agents.filter(a => a.status === "break").length },
  ];
  return (
    <div data-testid="admin-agents-panel">
      <RecordingSyncCard token={token} />
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, flex: 1 }}>
          {tiles.map(t => {
            const st = AGENT_TILE_STYLE[t.id];
            return (
              <div key={t.id} className="card" data-testid={`agent-tile-${t.id}`} style={{ padding: "12px 14px", borderLeft: `4px solid ${st.border}`, background: st.bg }}>
                <div style={{ fontSize: 10, color: "#555", textTransform: "uppercase", fontWeight: 600 }}>{t.label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, marginTop: 2 }}>{t.val}</div>
              </div>
            );
          })}
        </div>
        <button data-testid="agents-invite-btn" onClick={() => onOpenInvite?.()} className="btn btn-primary" style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}>
          <UserPlus style={{ width: 12, height: 12 }} /> Invite agent
        </button>
      </div>
      <div className="card" style={{ padding: 0 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead><tr style={{ background: "#fafafa", borderBottom: "0.5px solid #eee" }}>
            {["Agent", "Status", "Campaign", "Calls today", "Connected", "Meetings", "Avg dur.", "Actions"].map(h => (
              <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 10, fontWeight: 600, color: "#888", textTransform: "uppercase" }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {agents.length === 0 && (
              <tr><td colSpan={8} data-testid="agents-empty" style={{ padding: "24px 12px", textAlign: "center", color: "#888", fontSize: 12 }}>No agents connected yet.</td></tr>
            )}
            {agents.map(a => (
              <tr key={a.id} data-testid={`agent-row-${a.id}`} style={{ borderBottom: "0.5px solid #eee", cursor: "pointer" }} onClick={() => setDetailAgent(a)}>
                <td style={{ padding: "10px 14px" }}><div style={{ fontWeight: 500 }}>{a.name}</div><div style={{ fontSize: 10, color: "#888" }}>{a.role}</div></td>
                <td style={{ padding: "10px 14px" }}><span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 10, background: AGENT_TILE_STYLE[a.status]?.bg || "#f5f5f5", color: AGENT_TILE_STYLE[a.status]?.border || "#888", fontWeight: 500 }}>{a.status}</span></td>
                <td style={{ padding: "10px 14px", color: "#888" }}>{a.campaign || "—"}</td>
                <td style={{ padding: "10px 14px", fontWeight: 600 }}>
                  <button
                    data-testid={`agent-calls-today-btn-${a.id}`}
                    onClick={(e) => { e.stopPropagation(); setDetailAgent(a); }}
                    title="Click to see the individual calls that make up this number"
                    style={{
                      background: "none", border: "none", padding: 0,
                      fontFamily: "inherit", fontSize: "inherit",
                      fontWeight: 600, cursor: "pointer",
                      color: (a.callsToday ?? 0) > 0 ? "#185FA5" : "#94a3b8",
                      textDecoration: (a.callsToday ?? 0) > 0 ? "underline dotted" : "none",
                    }}
                  >
                    {a.callsToday ?? 0}
                  </button>
                </td>
                <td style={{ padding: "10px 14px" }}>{a.connected ?? 0}</td>
                <td style={{ padding: "10px 14px", color: "#1D9E75", fontWeight: 600 }}>{a.meetings ?? 0}</td>
                <td style={{ padding: "10px 14px" }}>{a.avgDuration || "—"}</td>
                <td style={{ padding: "10px 14px" }} onClick={e => e.stopPropagation()}>
                  <button data-testid={`agent-reassign-btn-${a.id}`} onClick={() => { setReassignFor(a); setReassignTarget(""); }} style={{ border: "0.5px solid #ddd", borderRadius: 6, padding: "3px 10px", fontSize: 10, background: "#fff", cursor: "pointer", fontFamily: "var(--font)" }}>Reassign</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Reassign popover (simple modal) */}
      {reassignFor && (
        <div data-testid="agent-reassign-modal" style={{ position: "fixed", inset: 0, background: "rgba(15,24,40,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300 }}>
          <div style={{ background: "#fff", borderRadius: 10, width: 380, padding: 18 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Reassign {reassignFor.name}</div>
            <label style={{ fontSize: 11, color: "#888" }}>Target campaign</label>
            <select data-testid="reassign-campaign-select" value={reassignTarget} onChange={e => setReassignTarget(e.target.value)} style={{ width: "100%", padding: "7px 10px", border: "0.5px solid #ddd", borderRadius: 6, fontSize: 13, marginTop: 4, fontFamily: "var(--font)" }}>
              <option value="">Select…</option>
              {campaigns.filter(c => c.status === "active").map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
              <button onClick={() => setReassignFor(null)} className="btn" style={{ fontSize: 12 }}>Cancel</button>
              <button data-testid="reassign-confirm-btn" onClick={doReassign} disabled={!reassignTarget} className="btn btn-primary" style={{ fontSize: 12, opacity: reassignTarget ? 1 : 0.5 }}>Reassign now</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail side panel */}
      {detailAgent && (
        <AgentDetailSidePanel
          agent={detailAgent}
          token={token}
          onClose={() => setDetailAgent(null)}
        />
      )}
    </div>
  );
}


/**
 * Iter 195.12 — Recording Sync mini-card.
 *
 * Shows the last N runs of the RC recording back-fill loops
 * (attach: every 5 min, retry: every 6 h) and lets an admin trigger
 * either sweep on demand. Answers the "why does my connected call
 * from 10 min ago have no ▶ Rec link?" question without spelunking
 * backend logs.
 *
 * Endpoints used:
 *   GET  /api/admin/rc-recording-sweep/logs
 *   POST /api/admin/rc-recording-sweep/run?kind=retry|attach
 */
function RecordingSyncCard({ token }) {
  const [logs, setLogs]       = useState([]);
  const [meta, setMeta]       = useState({});
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null); // "attach" | "retry" | null
  const [err, setErr]         = useState(null);
  const [expanded, setExpanded] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await axios.get(
        `${API_BASE}/admin/rc-recording-sweep/logs?limit=20`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setLogs(Array.isArray(r.data?.logs) ? r.data.logs : []);
      setMeta({
        loops_can_run:       r.data?.loops_can_run,
        attach_interval_sec: r.data?.attach_interval_sec,
        retry_interval_sec:  r.data?.retry_interval_sec,
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load sweep logs");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (token) refresh(); }, [token, refresh]);

  const runSweep = async (kind) => {
    setRunning(kind);
    try {
      const r = await axios.post(
        `${API_BASE}/admin/rc-recording-sweep/run?kind=${kind}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        `Sweep done: attached ${r.data?.counts?.attached ?? 0} recording(s)`,
      );
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sweep failed");
    } finally {
      setRunning(null);
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const today = new Date();
      const sameDay = d.toDateString() === today.toDateString();
      return sameDay
        ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
        : d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch { return iso; }
  };

  const latest = logs[0];
  const dormantBadge = meta.loops_can_run === false;

  return (
    <div
      data-testid="recording-sync-card"
      className="card"
      style={{ padding: "10px 14px", marginBottom: 12, background: "#F9FAFC", border: "1px solid #E5E7EB" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#3C3489" }}>Recording sync</div>
        {dormantBadge && (
          <span
            data-testid="recording-sync-dormant"
            style={{ fontSize: 10, background: "#FEE2E2", color: "#991B1B", padding: "2px 6px", borderRadius: 4 }}
          >
            dormant — no RC tokens
          </span>
        )}
        <div style={{ fontSize: 11, color: "#64748b", flex: 1, minWidth: 220 }}>
          {latest ? (
            <>
              last {latest.kind} run · {fmtTime(latest.ran_at)} ·
              {" "}attached {latest.counts?.attached ?? 0}
              {typeof latest.counts?.stuck === "number"
                ? ` / ${latest.counts.stuck} stuck`
                : ""}
              {latest.error ? <span style={{ color: "#b91c1c" }}> · error</span> : null}
            </>
          ) : loading ? "loading…" : "no sweeps recorded yet"}
        </div>
        <button
          data-testid="recording-sync-run-retry"
          onClick={() => runSweep("retry")}
          disabled={running !== null}
          className="btn"
          style={{ fontSize: 11, padding: "4px 10px", opacity: running ? 0.6 : 1 }}
        >
          {running === "retry" ? "Running…" : "Run retry sweep"}
        </button>
        <button
          data-testid="recording-sync-run-attach"
          onClick={() => runSweep("attach")}
          disabled={running !== null}
          className="btn"
          style={{ fontSize: 11, padding: "4px 10px", opacity: running ? 0.6 : 1 }}
        >
          {running === "attach" ? "Running…" : "Run attach sweep"}
        </button>
        <button
          data-testid="recording-sync-toggle"
          onClick={() => setExpanded(v => !v)}
          className="btn"
          style={{ fontSize: 11, padding: "4px 10px" }}
        >
          {expanded ? "Hide log" : "Show log"}
        </button>
      </div>

      {err && (
        <div style={{ marginTop: 8, fontSize: 11, color: "#b91c1c" }}>{err}</div>
      )}

      {expanded && (
        <div
          data-testid="recording-sync-log"
          style={{ marginTop: 10, maxHeight: 260, overflowY: "auto", background: "#fff", border: "1px solid #E5E7EB", borderRadius: 6 }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: "#F1F5F9", textAlign: "left" }}>
                <th style={{ padding: "6px 8px" }}>When</th>
                <th style={{ padding: "6px 8px" }}>Kind</th>
                <th style={{ padding: "6px 8px" }}>Trigger</th>
                <th style={{ padding: "6px 8px", textAlign: "right" }}>Attached</th>
                <th style={{ padding: "6px 8px", textAlign: "right" }}>Stuck / Reps</th>
                <th style={{ padding: "6px 8px", textAlign: "right" }}>RC records</th>
                <th style={{ padding: "6px 8px" }}>Errors</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 && !loading && (
                <tr><td colSpan={7} style={{ padding: "10px 8px", color: "#64748b" }}>No sweeps recorded yet — click &ldquo;Run retry sweep&rdquo; to seed.</td></tr>
              )}
              {logs.map(l => (
                <tr key={l.id} style={{ borderTop: "1px solid #E5E7EB" }}>
                  <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>{fmtTime(l.ran_at)}</td>
                  <td style={{ padding: "6px 8px" }}>{l.kind}</td>
                  <td style={{ padding: "6px 8px" }}>{l.trigger}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 600, color: (l.counts?.attached || 0) > 0 ? "#1D9E75" : "#64748b" }}>
                    {l.counts?.attached ?? 0}
                  </td>
                  <td style={{ padding: "6px 8px", textAlign: "right" }}>
                    {l.kind === "retry"
                      ? (l.counts?.stuck ?? "—")
                      : (l.counts?.reps_processed ?? "—")}
                  </td>
                  <td style={{ padding: "6px 8px", textAlign: "right" }}>
                    {l.counts?.rc_calls_fetched ?? l.counts?.rc_records ?? "—"}
                  </td>
                  <td style={{ padding: "6px 8px", color: l.error ? "#b91c1c" : "#64748b" }}>
                    {l.error
                      ? l.error.slice(0, 60)
                      : ((l.counts?.rc_401_403_errors || 0) + (l.counts?.rc_fetch_errors || 0)) || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}



/**
 * Iter 195.7 — Agent drill-down side panel. Renders the summary
 * ("Today" block) plus a scrollable list of the individual rc_call
 * rows that make up the agent's `callsToday` badge. Lets managers
 * verify "how did ioannis get to 91 calls?" by seeing time,
 * direction, outcome, duration, linked lead, and recording per row.
 */
function AgentDetailSidePanel({ agent, token, onClose }) {
  const [range, setRange]     = useState("today");
  const [loading, setLoading] = useState(true);
  const [calls, setCalls]     = useState([]);
  const [err, setErr]         = useState(null);

  useEffect(() => {
    if (!agent?.id || !token) return;
    let alive = true;
    setLoading(true);
    setErr(null);
    (async () => {
      try {
        const r = await axios.get(
          `${API_BASE}/dialer/agents/${agent.id}/calls?range=${range}&limit=500`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!alive) return;
        setCalls(Array.isArray(r.data?.calls) ? r.data.calls : []);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || e?.message || "Failed to load calls");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [agent?.id, range, token]);

  const fmtDur = (s) => {
    if (!s || s < 0) return "—";
    const m = Math.floor(s / 60), sec = s % 60;
    return `${m}:${String(sec).padStart(2, "0")}`;
  };
  const fmtTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    } catch { return "—"; }
  };
  const outcomeColor = (o) => {
    const s = (o || "").toLowerCase();
    if (s === "connected") return "#1D9E75";
    if (s === "voicemail" || s === "vm") return "#BA7517";
    if (s === "no-answer" || s === "busy" || s === "disconnected") return "#94a3b8";
    return "#475569";
  };

  return (
    <div
      data-testid="agent-detail-panel"
      style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: 480,
        background: "#fff", boxShadow: "-4px 0 12px rgba(0,0,0,0.1)",
        zIndex: 250, overflowY: "auto", padding: 18,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
        <div style={{ fontSize: 15, fontWeight: 600, flex: 1 }}>{agent.name}</div>
        <button
          data-testid="agent-detail-close"
          onClick={onClose}
          style={{ background: "none", border: "none", cursor: "pointer" }}
        >
          <X style={{ width: 14, height: 14 }} />
        </button>
      </div>
      <div style={{ fontSize: 11, color: "#888", marginBottom: 12 }}>
        {agent.role} ·{" "}
        <span style={{ color: AGENT_TILE_STYLE[agent.status]?.border || "#888" }}>
          {agent.status}
        </span>
      </div>

      <div style={{ background: "#fafafa", padding: 12, borderRadius: 8, marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", marginBottom: 6 }}>
          Today
        </div>
        <div style={{ fontSize: 12 }}>Calls: <strong>{agent.callsToday ?? 0}</strong></div>
        <div style={{ fontSize: 12 }}>Connected: <strong>{agent.connected ?? 0}</strong></div>
        <div style={{ fontSize: 12 }}>Meetings: <strong>{agent.meetings ?? 0}</strong></div>
        <div style={{ fontSize: 12 }}>Avg duration: <strong>{agent.avgDuration || "—"}</strong></div>
        <div style={{ fontSize: 12 }}>Current campaign: <strong>{agent.campaign || "—"}</strong></div>
      </div>

      {/* Range toggle */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", flex: 1 }}>
          Calls · {calls.length}
        </div>
        {["today", "7d", "30d"].map((r) => (
          <button
            key={r}
            data-testid={`agent-detail-range-${r}`}
            onClick={() => setRange(r)}
            style={{
              fontSize: 10, padding: "3px 8px", borderRadius: 6,
              border: "1px solid #e2e8f0", background: range === r ? "#3C3489" : "#fff",
              color: range === r ? "#fff" : "#475569", cursor: "pointer",
            }}
          >
            {r}
          </button>
        ))}
      </div>

      {loading && (
        <div style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#94a3b8" }}>
          Loading…
        </div>
      )}
      {!loading && err && (
        <div style={{ padding: 12, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, fontSize: 12, color: "#b91c1c" }}>
          {err}
        </div>
      )}
      {!loading && !err && calls.length === 0 && (
        <div style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#94a3b8" }}>
          No calls in this range.
        </div>
      )}
      {!loading && !err && calls.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {calls.map((c) => (
            <div
              key={c.id}
              data-testid={`agent-call-row-${c.id}`}
              style={{
                padding: 10, border: "1px solid #f1f5f9", borderRadius: 8, background: "#fff",
                display: "grid", gridTemplateColumns: "48px 1fr auto", gap: 8, alignItems: "center",
              }}
            >
              <div style={{ fontSize: 10, color: "#64748b", fontVariantNumeric: "tabular-nums" }}>
                {fmtTime(c.started_at)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {c.lead_name || c.to_number || "Unknown lead"}
                </div>
                <div style={{ fontSize: 10, color: "#64748b", display: "flex", gap: 8, marginTop: 2 }}>
                  <span style={{ color: outcomeColor(c.outcome), fontWeight: 600 }}>
                    {c.outcome || "—"}
                  </span>
                  <span>· {fmtDur(c.duration_sec)}</span>
                  {c.is_ivr && <span style={{ color: "#BA7517" }}>· IVR</span>}
                  {c.is_live_human && <span style={{ color: "#1D9E75" }}>· live</span>}
                  <span style={{ color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis" }}>
                    · {c.to_number}
                  </span>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-end" }}>
                {c.playback_url && (
                  <button
                    data-testid={`agent-call-rec-${c.id}`}
                    onClick={async (e) => {
                      e.stopPropagation();
                      // Authenticated fetch — the RC proxy requires
                      // Bearer JWT which a plain <a href> can't attach.
                      // We stream the bytes, wrap them in a blob URL,
                      // and open the audio in a fresh tab so it plays
                      // inline in the browser's default audio viewer.
                      try {
                        // c.playback_url from the backend already starts
                        // with `/api/...` — API_BASE also ends in `/api`,
                        // so concatenating them produced `/api/api/...`
                        // and 404'd. Use the raw backend origin here.
                        const origin = process.env.REACT_APP_BACKEND_URL || "";
                        const resp = await fetch(`${origin}${c.playback_url}`, {
                          headers: { Authorization: `Bearer ${token}` },
                        });
                        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                        const blob = await resp.blob();
                        const objectUrl = URL.createObjectURL(blob);
                        window.open(objectUrl, "_blank", "noopener");
                        // Revoke after 5 min so we don't leak audio
                        // buffers if the manager keeps clicking through
                        // recordings.
                        setTimeout(() => URL.revokeObjectURL(objectUrl), 5 * 60 * 1000);
                      } catch (err) {
                        alert(`Couldn't load recording: ${err.message || err}`);
                      }
                    }}
                    title="Play recording"
                    style={{
                      fontSize: 10, color: "#3C3489", textDecoration: "none",
                      background: "none", border: "none", padding: 0, cursor: "pointer",
                    }}
                  >
                    ▶ Rec
                  </button>
                )}
                {!c.playback_url && (c.outcome || "").toLowerCase() === "connected" && (() => {
                  const startedMs = c.started_at ? new Date(c.started_at).getTime() : 0;
                  const ageH = startedMs ? (Date.now() - startedMs) / 3.6e6 : 999;
                  return ageH >= 0 && ageH < 15 ? (
                    <span
                      data-testid={`agent-call-rec-pending-${c.id}`}
                      title="RingCentral publishes recording metadata a few minutes after the call ends. The background sync runs every 5 min (primary) + 6 h (retry)."
                      style={{ fontSize: 10, color: "#BA7517", fontStyle: "italic" }}
                    >
                      Recording ready soon…
                    </span>
                  ) : null;
                })()}
                {c.lead_id && (
                  <a
                    href={`/lead-focus/${c.lead_id}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: 10, color: "#185FA5", textDecoration: "none" }}
                  >
                    Open lead →
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ═════════════ ANALYTICS ═════════════
// Quick-range → { date_from, date_to } ISO strings. `all` returns {} so the
// backend returns the full pipeline dataset.
const DATE_RANGES = {
  today: () => ({ date_from: new Date(new Date().setHours(0,0,0,0)).toISOString(), date_to: new Date().toISOString() }),
  "7d":  () => ({ date_from: new Date(Date.now() - 7*864e5).toISOString(),  date_to: new Date().toISOString() }),
  "30d": () => ({ date_from: new Date(Date.now() - 30*864e5).toISOString(), date_to: new Date().toISOString() }),
  // Kept for deep-links coming from older UI revisions
  week:    () => ({ date_from: new Date(Date.now() - 7*864e5).toISOString(),  date_to: new Date().toISOString() }),
  month:   () => ({ date_from: new Date(Date.now() - 30*864e5).toISOString(), date_to: new Date().toISOString() }),
  quarter: () => ({ date_from: new Date(Date.now() - 90*864e5).toISOString(), date_to: new Date().toISOString() }),
  all:     () => ({}),
};

// ── Mini-calendar filter (Feature 2) ──
// Compact chip row: Today / 7d / 30d / Custom. Custom opens from/to date
// inputs. State is mirrored into the URL via `?range=...` (or
// `?from=YYYY-MM-DD&to=YYYY-MM-DD` for custom) so back/forward/refresh
// preserves the filter and the view is shareable.
const QUICK_CHIPS = [
  { id: "today", label: "Today" },
  { id: "7d",    label: "7d" },
  { id: "30d",   label: "30d" },
  { id: "custom", label: "Custom" },
];

// Custom ranges are capped at 60 days (inclusive) so the per-day chart stays readable.
const MAX_CUSTOM_DAYS = 60;
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const shiftDay = (ymd, days) => { const d = new Date(ymd + "T00:00:00"); d.setDate(d.getDate() + days); return isoDay(d); };
const spanDays = (from, to) => Math.round((new Date(to + "T00:00:00") - new Date(from + "T00:00:00")) / 864e5) + 1;

function MiniCalendarFilter({ value, onChange }) {
  // `value` shape: { range: "today"|"7d"|"30d"|"custom", from?: "YYYY-MM-DD", to?: "YYYY-MM-DD" }
  const { range, from, to } = value;
  const today = isoDay(new Date());
  const setFrom = (f) => {
    let t = to;
    if (f && t && (f > t || spanDays(f, t) > MAX_CUSTOM_DAYS)) {
      t = [shiftDay(f, MAX_CUSTOM_DAYS - 1), today].sort()[0];
      toast.info(`Custom ranges are limited to ${MAX_CUSTOM_DAYS} days — end date moved to ${t}`);
    }
    onChange({ range: "custom", from: f, to: t });
  };
  const setTo = (t) => {
    let f = from;
    if (f && t && (f > t || spanDays(f, t) > MAX_CUSTOM_DAYS)) {
      f = shiftDay(t, -(MAX_CUSTOM_DAYS - 1));
      toast.info(`Custom ranges are limited to ${MAX_CUSTOM_DAYS} days — start date moved to ${f}`);
    }
    onChange({ range: "custom", from: f, to: t });
  };
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="analytics-mini-calendar">
      <Calendar style={{ width: 12, height: 12, color: "#2563eb" }} />
      <div style={{ display: "inline-flex", background: "var(--gray-light, #f5f6f8)", borderRadius: 6, padding: 2 }}>
        {QUICK_CHIPS.map(c => {
          const active = range === c.id;
          return (
            <button
              key={c.id}
              data-testid={`analytics-range-${c.id}`}
              onClick={() => onChange({ range: c.id, from: from || "", to: to || "" })}
              style={{
                padding: "3px 10px", borderRadius: 4, fontSize: 11,
                fontWeight: active ? 600 : 400,
                background: active ? "#fff" : "transparent",
                color: active ? "var(--text-primary, #111)" : "var(--text-tertiary, #888)",
                border: "none", cursor: "pointer", fontFamily: "var(--font)",
              }}
            >{c.label}</button>
          );
        })}
      </div>
      {range === "custom" && (
        <>
          <input
            type="date"
            data-testid="analytics-custom-from"
            value={from || ""}
            max={to || today}
            min={to ? shiftDay(to, -(MAX_CUSTOM_DAYS - 1)) : undefined}
            onChange={e => setFrom(e.target.value)}
            style={{ border: "0.5px solid #ddd", borderRadius: 6, padding: "3px 6px", fontSize: 11, fontFamily: "var(--font)" }}
          />
          <span style={{ fontSize: 11, color: "#888" }}>to</span>
          <input
            type="date"
            data-testid="analytics-custom-to"
            value={to || ""}
            min={from || undefined}
            max={from ? [shiftDay(from, MAX_CUSTOM_DAYS - 1), today].sort()[0] : today}
            onChange={e => setTo(e.target.value)}
            style={{ border: "0.5px solid #ddd", borderRadius: 6, padding: "3px 6px", fontSize: 11, fontFamily: "var(--font)" }}
          />
        </>
      )}
    </div>
  );
}

// Compute { date_from, date_to } from the mini-calendar value. For
// `custom` without both endpoints, we fall back to the last 30 days so
// charts render sensibly during mid-picking.
function computeRangeParams({ range, from, to }) {
  if (range === "custom") {
    if (from && to) {
      // Enforce the 60-day cap even for hand-edited ?from/&to URLs.
      if (spanDays(from, to) > MAX_CUSTOM_DAYS) from = shiftDay(to, -(MAX_CUSTOM_DAYS - 1));
      const f = new Date(from + "T00:00:00");
      const t = new Date(to + "T23:59:59");
      if (!isNaN(f) && !isNaN(t) && f <= t) {
        return { date_from: f.toISOString(), date_to: t.toISOString() };
      }
    }
    return DATE_RANGES["30d"]();
  }
  if (DATE_RANGES[range]) return DATE_RANGES[range]();
  return DATE_RANGES["30d"]();
}

const SKELETON_BAR_HEIGHTS = [45, 70, 55, 85, 40, 65, 90, 50, 75, 60, 35, 80, 55, 70];

function ChartSkeleton() {
  return (
    <div data-testid="analytics-chart-skeleton" style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 140, marginTop: 12 }}>
      {SKELETON_BAR_HEIGHTS.map((h, i) => (
        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
          <div style={{ width: "100%", height: 110, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
            <div className="ehi-shimmer" style={{ width: "70%", height: `${h}%`, borderRadius: "4px 4px 0 0" }} />
          </div>
          <div className="ehi-shimmer" style={{ width: 18, height: 8 }} />
        </div>
      ))}
    </div>
  );
}

function DispositionSkeleton() {
  return (
    <div data-testid="analytics-disposition-skeleton">
      {[70, 55, 62, 45, 58, 40].map((w, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <div className="ehi-shimmer" style={{ width: 8, height: 8, borderRadius: "50%" }} />
          <div style={{ flex: 1 }}><div className="ehi-shimmer" style={{ width: `${w}%`, height: 10 }} /></div>
          <div className="ehi-shimmer" style={{ width: 100, height: 6, borderRadius: 3 }} />
          <div className="ehi-shimmer" style={{ width: 32, height: 10 }} />
        </div>
      ))}
    </div>
  );
}

function TableSkeletonRows() {
  return [0, 1, 2, 3, 4, 5].map(i => (
    <tr key={i} data-testid={`analytics-table-skeleton-${i}`} style={{ borderBottom: "0.5px solid #f5f5f5" }}>
      {["75%", "45%", "35%", "45%", "45%", "45%", "60%"].map((w, j) => (
        <td key={j} style={{ padding: "10px" }}><div className="ehi-shimmer" style={{ width: w, height: 10 }} /></td>
      ))}
    </tr>
  ));
}

export function AdminAnalyticsPanel({ campaigns, agents, token }) {
  const [searchParams, setSearchParams] = useSearchParams();
  // Hydrate from URL so refresh / back-forward preserves the filter.
  const initialRange = (() => {
    const r = searchParams.get("range");
    if (r && QUICK_CHIPS.some(c => c.id === r)) return r;
    // Legacy deep-link: `?range=week|month|quarter|all` still works.
    if (r === "week" || r === "month") return r === "week" ? "7d" : "30d";
    if (searchParams.get("from") && searchParams.get("to")) return "custom";
    return "30d";
  })();
  const [rangeState, setRangeState] = useState(() => {
    const to = searchParams.get("to") || "";
    let from = searchParams.get("from") || "";
    // Deep links get the same 60-day cap as the pickers so inputs, URL and data agree.
    if (from && to && spanDays(from, to) > MAX_CUSTOM_DAYS) from = shiftDay(to, -(MAX_CUSTOM_DAYS - 1));
    return { range: initialRange, from, to };
  });
  const [campaignId, setCampaignId] = useState(searchParams.get("campaign_id") || "");
  const [campaignName, setCampaignName] = useState("");
  const [agentId, setAgentId] = useState(searchParams.get("agent_id") || "");
  const [scopedAgents, setScopedAgents] = useState(null);
  const selectCampaign = (id, name) => { setCampaignId(id); setCampaignName(name || ""); };
  const selectedCampaignName = campaignName || campaigns.find(c => c.id === campaignId)?.name || "";
  // With a campaign + agent chosen, wait for the campaign's agent list so a stale agent is reset before loading.
  const rangeKey = JSON.stringify(rangeState);
  const agentScopeReady = !campaignId || !agentId || (scopedAgents?.campaign_id === campaignId && scopedAgents?.rangeKey === rangeKey);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllDisp, setShowAllDisp] = useState(false);
  const [showAllStages, setShowAllStages] = useState(false);

  // Keep URL in sync (replace, not push, so we don't spam history).
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("range", rangeState.range);
    if (rangeState.range === "custom") {
      if (rangeState.from) next.set("from", rangeState.from); else next.delete("from");
      if (rangeState.to)   next.set("to",   rangeState.to);   else next.delete("to");
    } else {
      next.delete("from"); next.delete("to");
    }
    if (campaignId) next.set("campaign_id", campaignId); else next.delete("campaign_id");
    if (agentId)    next.set("agent_id",    agentId);    else next.delete("agent_id");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeState, campaignId, agentId]);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setData(null);
    if (!agentScopeReady) return () => { cancel = true; };
    const run = async () => {
      try {
        const params = { ...computeRangeParams(rangeState) };
        if (campaignId) params.campaign_id = campaignId;
        if (agentId) params.agent_id = agentId;
        const res = await axios.get(`${API_BASE}/dialer/analytics`, { params, headers: { Authorization: `Bearer ${token}` } });
        if (!cancel) setData(res.data);
      } catch (err) {
        if (!cancel) toast.error(err?.response?.data?.detail || "Analytics load failed");
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    run();
    return () => { cancel = true; };
  }, [rangeState, campaignId, agentId, token, campaigns, agentScopeReady]);

  // Agent filter scoped to the selected campaign: assigned + anyone else who worked it in range.
  useEffect(() => {
    if (!campaignId) { setScopedAgents(null); return undefined; }
    let cancel = false;
    setScopedAgents(null);
    (async () => {
      try {
        const { data: d } = await axios.get(`${API_BASE}/dialer/analytics/campaign-agents`, {
          params: { campaign_id: campaignId, ...computeRangeParams(rangeState) }, headers: { Authorization: `Bearer ${token}` },
        });
        if (cancel) return;
        setScopedAgents({ ...d, campaign_id: campaignId, rangeKey: JSON.stringify(rangeState) });
        const ids = new Set([...(d.assigned || []), ...(d.also_worked || [])].map(a => a.id));
        setAgentId(cur => (cur && !ids.has(cur) ? "" : cur));
      } catch (_e) {
        if (!cancel) setScopedAgents({ campaign_id: campaignId, rangeKey: JSON.stringify(rangeState), assigned: [], also_worked: [], failed: true });
      }
    })();
    return () => { cancel = true; };
  }, [campaignId, rangeState, token]);


  const teamAvgConversion = useMemo(() => {
    if (!data?.campaign_performance?.length) return 0;
    return data.campaign_performance.reduce((s, r) => s + r.conversion_rate, 0) / data.campaign_performance.length;
  }, [data]);

  return (
    <div data-testid="admin-analytics-panel">
      {/* Filter bar */}
      <div data-testid="analytics-filter-bar" style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, padding: "10px 12px", background: "#fff", border: "0.5px solid #eee", borderRadius: 10, flexWrap: "wrap" }}>
        <MiniCalendarFilter value={rangeState} onChange={setRangeState} />
        <div style={{ width: 1, height: 16, background: "#eee" }} />
        <Filter style={{ width: 12, height: 12, color: "#888" }} />
        <CampaignSearchFilter value={campaignId} selectedName={selectedCampaignName} onChange={selectCampaign} token={token} />
        <select data-testid="analytics-agent-filter" value={agentId} onChange={e => setAgentId(e.target.value)} style={{ border: "0.5px solid #ddd", borderRadius: 6, padding: "4px 8px", fontSize: 12, fontFamily: "var(--font)" }}>
          <option value="">All agents</option>
          {campaignId && scopedAgents && !scopedAgents.failed ? (
            <>
              <optgroup label="Assigned agents">
                {(scopedAgents.assigned || []).map(a => {
                  const ag = agents.find(x => x.id === a.id);
                  return <option key={a.id} value={a.id}>{`${ag?.name || a.name} · ${ag?.callsToday ?? 0}`}</option>;
                })}
              </optgroup>
              {(scopedAgents.also_worked || []).length > 0 && (
                <optgroup label="Also worked this campaign">
                  {scopedAgents.also_worked.map(a => <option key={a.id} value={a.id}>{agents.find(x => x.id === a.id)?.name || a.name}</option>)}
                </optgroup>
              )}
            </>
          ) : agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>

      {/* Overview tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 14 }}>
        {[
          { label: "Total calls", val: (data?.overview?.total_calls ?? 0).toLocaleString(), color: "#2563eb" },
          { label: "Human connect rate", val: `${data?.overview?.human_connect_rate ?? data?.overview?.connect_rate ?? 0}%`, color: "#1D9E75" },
          { label: "Meetings booked", val: data?.overview?.meetings_booked ?? 0, color: "#A78BFA" },
          { label: "Avg duration", val: data?.overview?.avg_duration_label ?? "—", color: "#ff9500" },
          { label: "Campaigns tracked", val: data?.overview?.campaigns_tracked ?? data?.campaign_performance?.length ?? 0, color: "#185FA5" },
        ].map((s, i) => loading ? (
          <div key={i} className="card" style={{ padding: "14px 16px" }} data-testid={`analytics-tile-skeleton-${i}`}>
            <div className="ehi-shimmer" style={{ width: "55%", height: 10, marginTop: 2 }} />
            <div className="ehi-shimmer" style={{ width: "40%", height: 22, marginTop: 8 }} />
          </div>
        ) : (
          <div key={i} className="card" style={{ padding: "14px 16px" }} data-testid={`analytics-tile-${i}`}>
            <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase" }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.color, marginTop: 4 }}>{s.val}</div>
          </div>
        ))}
      </div>

      {/* 2-col grid — left charts, right campaign perf table */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div className="card" style={{ padding: "16px 18px", marginBottom: 12 }}>
            <div className="card-title">Calls by day</div>
            {loading ? <ChartSkeleton /> : (data?.calls_by_day?.length || 0) === 0 ? (
              <div data-testid="analytics-calls-empty" style={{ fontSize: 11, color: "#888", padding: "24px 0", textAlign: "center" }}>No calls logged in this range.</div>
            ) : (
              <div data-testid="analytics-calls-chart-scroll" style={{ overflowX: "auto", overflowY: "hidden", marginTop: 12, paddingBottom: 4 }}>
              <div data-testid="analytics-calls-chart" style={{ display: "flex", alignItems: "flex-end", gap: data.calls_by_day.length > 15 ? 3 : 6, height: 140, minWidth: data.calls_by_day.length * 18 }}>
                {(() => {
                  const max = Math.max(1, ...(data.calls_by_day.map(d => d.calls)));
                  // Long ranges: label every Nth day so date labels don't overlap.
                  const n = data.calls_by_day.length;
                  const labelStep = n > 15 ? Math.ceil(n / 12) : 1;
                  return data.calls_by_day.map((d, i) => (
                    <div key={i} style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, position: "relative" }} title={`${d.date}: ${d.calls} calls · ${d.meetings} meetings`}>
                      <div style={{ position: "relative", width: "100%", height: 110, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
                        <div style={{ width: "70%", height: `${(d.calls / max) * 100}%`, background: "#2563eb", borderRadius: "4px 4px 0 0", minHeight: 3 }} />
                        {d.meetings > 0 && (
                          <div style={{ position: "absolute", bottom: 0, width: "70%", height: `${(d.meetings / max) * 100}%`, background: "#1D9E75", borderRadius: "4px 4px 0 0", opacity: 0.9, minHeight: 4 }} />
                        )}
                      </div>
                      <span style={{ fontSize: 9, color: "#888", whiteSpace: "nowrap",
                        alignSelf: n > 15 && i === 0 ? "flex-start" : n > 15 && i === n - 1 ? "flex-end" : "center",
                        visibility: i === n - 1 || (i % labelStep === 0 && n - 1 - i >= labelStep) ? "visible" : "hidden" }}>{d.date.slice(5)}</span>
                    </div>
                  ));
                })()}
              </div>
              </div>
            )}
            <div style={{ display: "flex", gap: 12, marginTop: 8, fontSize: 10, color: "#888" }}>
              <span><span style={{ color: "#2563eb" }}>■</span> <span style={{ color: "#2563eb" }}>Calls</span></span>
              <span><span style={{ color: "#1D9E75" }}>■</span> <span style={{ color: "#1D9E75" }}>Meetings</span></span>
            </div>
          </div>
          <div className="card" style={{ padding: "16px 18px", marginBottom: 12 }} data-testid="analytics-lead-stage-card">
            <div className="card-title" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              Lead stage breakdown
              <CardTip testId="analytics-lead-stage-tip" align="left" title="Lead stage breakdown" items={[
                "Each lead dialed at least once in the selected range is counted once, under the pipeline stage it has today — not the stage it had when it was called.",
                "Stages are the 10 pipeline stages shown at the top of Lead Focus and in the Stage step of Log outcome: New Leads, Contacted, Demo, Post-Demo Follow-Up, Proposal, Decision, Closed Won, Implementation, Closed Lost, Inactive.",
                "Older stage names still stored on some leads (e.g. \"New Lead – Immediate Follow-Up\", \"new\", \"Qualification\") are counted under their matching pipeline stage. Leads with no stage are counted as New Leads.",
                "The Information tab's separate \"Lead Stage\" dropdown isn't used here.",
                "Follows the date range, campaign and agent filters.",
              ]} />
            </div>
            <div style={{ marginTop: 12 }}>
              {loading && <DispositionSkeleton />}
              {!loading && (data?.lead_stage_breakdown || []).slice(0, showAllStages ? undefined : 10).map((d, i) => (
                <div key={d.stage} data-testid={`analytics-lead-stage-${d.stage}`} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }} title={`${d.count} lead${d.count === 1 ? "" : "s"}`}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: ["#1D9E75","#2563eb","#ff9500","#8e8e93","#A78BFA"][i % 5] }} />
                  <span style={{ fontSize: 11, flex: 1 }}>{d.stage}</span>
                  <span style={{ fontSize: 11, color: "#555", width: 48, textAlign: "right" }}>{d.count.toLocaleString()}</span>
                  <div style={{ width: 100, height: 6, background: "#f5f5f5", borderRadius: 3 }}><div style={{ width: `${d.pct}%`, height: 6, background: ["#1D9E75","#2563eb","#ff9500","#8e8e93","#A78BFA"][i % 5], borderRadius: 3 }} /></div>
                  <span style={{ fontSize: 11, fontWeight: 600, width: 40, textAlign: "right" }}>{d.pct === 0 && d.count > 0 ? "<0.1%" : `${d.pct}%`}</span>
                </div>
              ))}
              {!loading && (data?.lead_stage_breakdown?.length || 0) === 0 && <div data-testid="analytics-lead-stage-empty" style={{ fontSize: 11, color: "#888" }}>No leads dialed in this range.</div>}
              {!loading && (data?.lead_stage_breakdown?.length || 0) > 10 && (
                <button data-testid="analytics-lead-stage-toggle" onClick={() => setShowAllStages(v => !v)} style={{ fontSize: 11, color: "#185FA5", background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "var(--font)" }}>
                  {showAllStages ? "Show top 10" : `Show all ${data.lead_stage_breakdown.length}`}
                </button>
              )}
            </div>
          </div>
          <div className="card" style={{ padding: "16px 18px" }}>
            <div className="card-title">Disposition breakdown</div>
            <div style={{ marginTop: 12 }}>
              {loading && <DispositionSkeleton />}
              {!loading && (data?.disposition_breakdown || []).slice(0, showAllDisp ? undefined : 10).map((d, i) => (
                <div key={d.disposition} data-testid={`analytics-disposition-${d.disposition}`} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: ["#1D9E75","#2563eb","#ff9500","#8e8e93","#A78BFA"][i % 5] }} />
                  <span style={{ fontSize: 11, flex: 1 }}>{d.disposition.replace(/_/g, " ")}</span>
                  <div style={{ width: 100, height: 6, background: "#f5f5f5", borderRadius: 3 }}><div style={{ width: `${d.pct}%`, height: 6, background: ["#1D9E75","#2563eb","#ff9500","#8e8e93","#A78BFA"][i % 5], borderRadius: 3 }} /></div>
                  <span style={{ fontSize: 11, fontWeight: 600, width: 40, textAlign: "right" }}>{d.pct}%</span>
                </div>
              ))}
              {!loading && (data?.disposition_breakdown?.length || 0) === 0 && <div style={{ fontSize: 11, color: "#888" }}>No outcomes recorded in this range.</div>}
              {!loading && (data?.disposition_breakdown?.length || 0) > 10 && (
                <button data-testid="analytics-disposition-toggle" onClick={() => setShowAllDisp(v => !v)} style={{ fontSize: 11, color: "#185FA5", background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "var(--font)" }}>
                  {showAllDisp ? "Show top 10" : `Show all ${data.disposition_breakdown.length}`}
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="card" style={{ padding: "16px 18px" }}>
          <div className="card-title" style={{ marginBottom: 10 }}>Campaign performance</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }} data-testid="analytics-campaign-table">
            <thead>
              <tr style={{ borderBottom: "0.5px solid #eee" }}>
                {["Campaign", "Calls", "Meetings", "Conv.", "Human connect", "Unique leads", ""].map(h => (
                  <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontSize: 10, fontWeight: 600, color: "#888", textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <TableSkeletonRows />}
              {!loading && (data?.campaign_performance || []).map(row => {
                const aboveAvg = row.conversion_rate >= teamAvgConversion;
                const viewing = row.campaign_id === campaignId;
                return (
                  <tr key={row.campaign_id} data-testid={`analytics-campaign-row-${row.campaign_id}`} style={{ borderBottom: "0.5px solid #f5f5f5", background: viewing ? "#EFF6FF" : undefined }}>
                    <td style={{ padding: "8px 10px", fontWeight: 500 }}>{row.campaign_name || row.campaign_id}</td>
                    <td style={{ padding: "8px 10px" }}>{row.calls}</td>
                    <td style={{ padding: "8px 10px", color: "#1D9E75", fontWeight: 600 }}>{row.meetings}</td>
                    <td style={{ padding: "8px 10px", color: aboveAvg ? "#1D9E75" : "#E24B4A", fontWeight: 600 }}>{row.conversion_rate}%</td>
                    <td data-testid={`analytics-campaign-hcr-${row.campaign_id}`} style={{ padding: "8px 10px", fontWeight: 600, color: "#1D9E75" }}>{row.human_connect_rate ?? 0}%</td>
                    <td data-testid={`analytics-campaign-unique-${row.campaign_id}`} style={{ padding: "8px 10px" }}>{(row.unique_leads ?? 0).toLocaleString()}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right" }}>
                      {viewing ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                          <span data-testid={`analytics-viewing-${row.campaign_id}`} style={{ fontSize: 10, fontWeight: 600, color: "#185FA5", background: "#DBEAFE", borderRadius: 10, padding: "2px 8px" }}>Viewing</span>
                          <button data-testid="analytics-viewing-clear" onClick={() => selectCampaign("", "")} style={{ fontSize: 11, color: "#185FA5", background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "var(--font)", textDecoration: "underline" }}>Clear</button>
                        </span>
                      ) : row.campaign_id !== "_unassigned" && <button data-testid={`analytics-drill-${row.campaign_id}`} onClick={() => selectCampaign(row.campaign_id, row.campaign_name)} style={{ fontSize: 11, color: "#185FA5", background: "none", border: "none", cursor: "pointer", fontFamily: "var(--font)" }}>Drill down →</button>}
                    </td>
                  </tr>
                );
              })}
              {!loading && (data?.campaign_performance?.length || 0) === 0 && (
                <tr><td colSpan={7} style={{ padding: "24px 10px", textAlign: "center", color: "#888", fontSize: 11 }}>No campaign activity in this range.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
